/* ==========================================================================
   Nordenheim — bundle CORE
   --------------------------------------------------------------------------
   ONE implementation of the bundle machinery, used by BOTH surfaces:

     sections/nordenheim-bundle-builder.liquid  — the bundle PDPs
     sections/nordenheim-plate-snap.liquid      — Mix & Match, in bundle mode

   What lives here is exactly the half the two surfaces share: an ORDERED SET
   of up to N chosen plates, the tray that draws it, the running count, the
   dynamic button text, the cart payload (bundle variant + N plate lines with
   `_nh_bundle` and `Part of`), the toast with its Undo, and the phone Tray
   Sheet's dock — its geometry, park/open, drag, page-inert, release and Add.

   What does NOT live here is the PICTURE: the stage, derive(), the snap, the
   wall markup, the cap chooser, the info popover and the gallery strip. Those
   differ between a page that holds ONE current plate (plate-snap: a radio
   group, two scalars) and a page that holds an ordered SET (the builder: an
   array, checkboxes, a tray). The half that differs is the picture; the half
   that is identical is the ordered set, and that is this file.

   HOW IT IS INSTALLED. These are plain object methods copied onto each
   section's PROTOTYPE, so `this` is the custom element itself and every
   reference below (`this.chosen`, `this.els.tray`, `this.labels`) means what
   it always meant. That is deliberate: the methods were MOVED out of
   nordenheim-bundle-builder.js unchanged rather than rewritten around an
   adapter object, so the seventeen rounds of measured fixes they carry — the
   descending fan order, the single-assignment `hidden` fix, the
   aria-disabled-not-disabled full-tray state, applyAway's scroll-measured
   `--nh-bb-away`, the captured `itemInputs` — moved with them, byte for byte.

   WHAT EACH HOST MUST PROVIDE (the adapter). Nothing here queries the
   document for a node it was not handed, and nothing here parses any JSON.

     state    chosen[] · max · slots[] · confirms[] · itemInputs[] ·
              previewButtons[] · previewed · viewCap · capKey · labels ·
              els{} · timers · rungs[] · mixLinks[] · on()
     methods  bcRoot() · plateAt(ci,pi) · capChosen() · preview(ref,opts) ·
              sheetActive() · compactWidth() · placeSheet() · revealWall() ·
              renderStrip() · paintGeometry() · syncCapRoving() ·
              setCompactStage(on) · bcSheetMove(active)
     flags    bcStageSwipe = false  when the host already owns a horizontal
                                    drag on the same stage element
              partName              when the `Part of` value is decided at
                                    runtime rather than server-rendered

   A host that needs to keep a method of its own with a core name declares it
   in `owns` — otherwise installBundleCore refuses to clobber it and says so,
   because a silent clobber here is a cart bug, not a styling one.
   ========================================================================== */

/* HOW IT IS LOADED, and why this is not an ES module.

   Both sections emit it as a plain deferred script with `asset_url`, which
   carries a `?v=<hash>`; a `type="module"` file importing "./…-core.js" would
   resolve to the SAME path WITHOUT that query, and CLAUDE.md records what an
   unversioned theme-asset URL does — the CDN kept serving the old bytes long
   after the push. A deferred classic script and a module script execute in
   document order, so emitting this one first guarantees the global exists
   before either section's module evaluates. Idempotent: two sections on one
   page emit two identical tags, and only the first one to run defines it.
   -------------------------------------------------------------------------- */

(function () {
if (window.NordenheimBundleCore) return;

/* --------------------------------------------------------------------------
   Module-level helpers, copied verbatim from nordenheim-bundle-builder.js so
   the moved bodies did not have to change. plate-snap has its own private
   copies of clamp/cssUrl/cssAttr for its own code; these are the core's.
   -------------------------------------------------------------------------- */

function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

/* Escape a URL for safe interpolation into a CSS url() token. */
function cssUrl(src) {
  return String(src).replace(/["\\]/g, '\\$&');
}

/* Fill {token} placeholders from a plain object. Missing keys are left alone
   rather than replaced with "undefined", so a mistyped token shows itself. */
function fill(template, values) {
  return String(template || '').replace(/\{(\w+)\}/g, (whole, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole
  );
}

/* Escape a value for safe interpolation into an attribute selector. ROUND 19:
   moved here with the cap control, which reads a row back by its key. */
function cssAttr(v) {
  return String(v).replace(/["\\]/g, '\\$&');
}

/* ROUND 19. The shortest the info card is allowed to be before it opens the
   other way instead. Below this it is a scrollbar with a sentence in it. */
const INFO_MIN_H = 180;

/* Words that are read but not shown — a paired caption keeps its whole
   sentence for a screen reader while showing only the two names and a ×.
   ROUND 19: moved here with paintCapNote(). */
function srText(text) {
  const el = document.createElement('span');
  el.className = 'nh-bb__sr';
  el.textContent = text;
  return el;
}

function thumbSrc(plate, capKey) {
  if (plate && plate.art) return plate.art;
  const entry = plate && plate.images && plate.images[capKey];
  if (!entry) return '';
  return typeof entry === 'string' ? entry : entry.thumb || entry.stage || '';
}

const NhBundleCore = {

  indexOfChoice(ci, pi) {
    return this.chosen.findIndex((c) => c.ci === ci && c.pi === pi);
  },

  previewedPlate() {
    return this.previewed ? this.plateAt(this.previewed.ci, this.previewed.pi) : null;
  },

  /**
   * ROUND 21, ITEM D — WHAT THE INFO CARD IS ABOUT.
   *
   * Usually the previewed plate. On a host that can show a CAP on its own (Mix
   * & Match's cap row) it is that cap, wearing the same four fields the card
   * reads: a name for the heading, a url for the link, a body, and a handle to
   * key the cache and the "did the subject change" test on. `cap:` prefixes the
   * handle so a cap and a plate can never collide in `descCache`.
   *
   * `isCap` is what picks the cap's own label and link wording, both of which
   * are settings with their own Swedish — "Art Plate" and "Art Cap" are product
   * names, not interchangeable words.
   *
   * The bundle PDPs define no `soloCap`, so this is `previewedPlate()` there and
   * every path below behaves exactly as it did.
   */
  capInfoSubject(cap) {
    if (!cap) return null;
    return {
      handle: `cap:${cap.key || ''}`,
      name: cap.name || '',
      url: cap.url || '',
      description: cap.description || '',
      isCap: true,
    };
  },

  infoSubject() {
    const solo = typeof this.soloCap === 'function' ? this.soloCap() : null;
    return solo ? this.capInfoSubject(solo) : this.previewedPlate();
  },

  /**
   * THE GROUND (round 11, Sina) — the caption and gallery bands continue the
   * photograph rather than sitting under it as two mismatched strips.
   *
   * The colours belong to the CAP RENDER, not to the plate: the same colourway
   * is pixel-identical across different plates, so eight pairs cover the whole
   * catalogue. `from` is the composite's background at the row the crop ends on
   * (91.0% of the square, which is what --nh-bb-stage-fill and
   * --nh-bb-stage-focus produce); `to` continues that gradient, damped, to the
   * foot of the gallery band.
   *
   * Keyed to `viewCap`, the DISPLAYED colourway — never `capKey`, which is what
   * is being bought. Looking at a plate on Timmer must tint the field for
   * Timmer whatever is in the cart, and the two have been separate state since
   * round 3 for exactly this class of mistake.
   *
   * A worn, editorial or packaging shot HOLDS the last cap's ground rather than
   * flashing to some other tint. Those photographs are not cap composites — they
   * have backgrounds of their own that no setting describes — and this falls out
   * of the state model rather than needing a branch: openShot() only calls
   * applyCap() when the shot records a colourway, so `viewCap` (and the ground
   * with it) simply does not move for the ones that do not.
   *
   * A cap block with neither colour set leaves the class off and the three-step
   * warm ladder in place, which is the fallback the CSS is still written for.
   */
  paintGround({ fade = false } = {}) {
    const view = this.els.view;
    if (!view) return;
    // The properties go on the <section>, not on the viewing zone and not on this
    // element: the phone's compact sticky header is a child of the SECTION (so
    // that nothing can clip a fixed box) and it paints the same ground. The
    // section is the nearest common ancestor of the two, so one set of values
    // serves both. The `is-grounded` CLASS stays on the zone — it decides whether
    // the bands take the gradient or the old warm ladder, and that is a decision
    // about the zone.
    // ROUND 21 — the root is asked of the host. The builder's values go on its
    // `.nh-bb` section (the phone's fixed compact header is a child of it and
    // paints the same ground); Mix & Match has no `.nh-bb` ancestor at all and
    // names its own custom element, which contains its zone, its deck and its
    // bottom sheet. `closest()` then `this` is what the builder always did, so
    // its behaviour is unchanged.
    const root = (typeof this.groundRoot === 'function' && this.groundRoot())
      || this.closest('.nh-bb')
      || this;
    // ROUND 21 — A CAP SHOWN ON ITS OWN HAS NO GROUND TO CONTINUE.
    //
    // Mix & Match's cap row puts the cap product's own photographs on the
    // stage, and those are TRANSPARENT palette PNGs — there is no background in
    // them to sample and nothing for the bands to carry on from. The measured
    // pairs describe the `-cap-<key>` COMPOSITE, which is not what is on screen.
    // So the zone drops to one flat house tone in that state (see the
    // `.nh-ps--solocap` rules in assets/nordenheim-plate-snap.css, which pin the
    // caption and the strip to the same `--nh-bb-view-bg` the picture sits on).
    //
    // Asked of the HOST: the bundle PDPs define no `soloCap`, so they can never
    // reach this branch.
    const solo = typeof this.soloCap === 'function' ? this.soloCap() : null;
    const cap = (solo ? null : this.cap(this.viewCap)) || {};
    const from = (cap.groundFrom || '').trim();
    const to = (cap.groundTo || '').trim();
    // A colour picker can hand back a fully transparent value, which is not a
    // ground — it is the merchant having cleared the field.
    const bad = (v) => !v || /^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/i.test(v);
    if (bad(from) || bad(to)) {
      view.classList.remove('is-grounded');
      root.style.removeProperty('--nh-bb-ground-from');
      root.style.removeProperty('--nh-bb-ground-to');
      return;
    }
    // The transition is declared in the stylesheet on .is-grounded, so the very
    // first application would fade in from the registered initial value. Zero it
    // for that paint, force the style to settle, then hand it back.
    if (!fade) {
      root.style.transition = 'none';
      root.style.setProperty('--nh-bb-ground-from', from);
      root.style.setProperty('--nh-bb-ground-to', to);
      view.classList.add('is-grounded');
      void root.offsetWidth;
      root.style.removeProperty('transition');
      return;
    }
    view.classList.add('is-grounded');
    root.style.setProperty('--nh-bb-ground-from', from);
    root.style.setProperty('--nh-bb-ground-to', to);
  },


  /**
   * The plate offered in the next empty slot, if there is one.
   *
   * Null when the tray is full, and null when the previewed plate is already in
   * it — you cannot be offered something you have.
   */
  candidate() {
    if (!this.previewed) return null;
    // ROUND 14. A phone opens with NO candidate. See preview() — the seeded
    // plate is on the stage, but offering it in the bar before the buyer has
    // touched anything reads as "one of my three is already chosen". Desktop is
    // unchanged: there the candidate sits in the panel's tray beside the
    // picture, where the dashed outline and the unticked box are legible.
    if (this.untouched && this.compactWidth()) return null;
    if (this.chosen.length >= this.max) return null;
    if (this.indexOfChoice(this.previewed.ci, this.previewed.pi) >= 0) return null;
    const plate = this.previewedPlate();
    if (!plate || plate.variantId == null || plate.available === false) return null;
    return this.previewed;
  },

  /** The slot the candidate occupies: always the first empty one. */
  candidateSlot() {
    return this.candidate() ? this.chosen.length : -1;
  },

  markPreviewed() {
    const cur = this.previewed;
    this.previewButtons.forEach((el) => {
      const on = !!cur && Number(el.dataset.cat) === cur.ci && Number(el.dataset.index) === cur.pi;
      el.setAttribute('aria-pressed', String(on));
    });
  },

  /**
   * ONE control per tile, in the same corner in every state (Sina, round 6).
   *
   * Ticking the candidate's box adds that plate; unticking a chosen tile's box
   * takes it out again. It replaced a checkbox for adding and a separate × for
   * removing — two controls, two shapes, two corners, for one fact: is this
   * plate in the bundle or not. A checkbox already means exactly that, so the
   * state IS the control and there is nothing extra to learn.
   *
   * The box is therefore a real state now, not the momentary trigger it was:
   * renderTray sets `checked` from `chosen`, and every early return puts the
   * input back the way the model says it should be.
   */
  onSlotToggle(input) {
    const slot = Number(input.dataset.nhBbConfirm);

    // ---- a chosen tile: unticking removes it ----------------------------
    if (this.chosen[slot]) {
      if (input.checked) return;          // nothing else can tick a filled box
      this.removeAt(slot);
      return;
    }

    // ---- the candidate: ticking commits it ------------------------------
    const cand = this.candidate();
    if (!input.checked || !cand || slot !== this.candidateSlot()) {
      input.checked = false;
      return;
    }
    const addedPlate = this.plateAt(cand.ci, cand.pi);
    if (!this.addPlate(cand)) return;
    // renderTray re-reads `chosen` and leaves this very box ticked, so focus
    // stays where the shopper put it — no focus rescue needed on this path.
    this.afterChange();
    // ROUND 16 (Sina, item 11) — a toast confirmation for the checkbox-confirm
    // path (desktop/band), giving it the same "Added ✓" moment the sheet's own
    // button already has on phones. Not routed through the sheet's mechanism —
    // that lives on a different button and already answers this moment there;
    // this is the surface that never had a confirmation at all.
    if (!this.sheetActive()) {
      const withName = addedPlate && addedPlate.name
        ? `${this.labels.addedConfirm || 'Added ✓'}, ${addedPlate.name}`
        : (this.labels.addedConfirm || 'Added ✓');
      this.say(withName, true);
    }
  },

  /**
   * ROUND 22 — the wall tile's own add control.
   *
   * A sibling of the tile (never a child — a button inside a <label> fires the
   * label's control too), addressed by the same {cat, index} pair the tile
   * carries, so `markChosen` can paint it in the same pass that paints the tile.
   *
   * Its accessible name IS its state: "Add Bästkusten" / "In your bundle —
   * remove" / the full-tray line, from the same three labels the sheet's button
   * uses, so the two controls can never describe the tray differently. The glyph
   * swap (plus / tick) is CSS off `is-chosen`; this writes the words.
   *
   * aria-disabled, not disabled — round 15.1's rule: a disabled button leaves
   * the tab order and takes focus with it, and the full-tray state is exactly
   * the one a keyboard user needs to be able to reach and hear.
   */
  /**
   * Bound from markChosen() rather than from bindSheet(), and that is the whole
   * bug this method exists to record: bindSheet() is "listeners the sheet needs,
   * bound once, the first time it becomes active — never on a page that stays
   * above 901px", so on a desktop "Plates only" it never runs and the wall's add
   * controls were dead. Measured 2026-09-04: onTileAdd hit 0 times on click
   * while calling it directly worked. markChosen() runs in every mode and on
   * every change, and the flag makes this idempotent.
   */
  bindTileAdd() {
    if (this.tileAddBound) return;
    //
    // Delegated rather than bound per tile because the wall is up to 106 tiles
    // long and because the panel that holds it is MOVED between the page, this
    // section's sheet and the dock; a listener on the root survives every one of
    // those moves, and one listener is one thing to reason about.
    //
    // preventDefault because the button sits inside the tile's positioned cell
    // and the click must not also reach the label — see the markup comment.
    const tileAddRoot = (this.bcRoot && this.bcRoot()) || this;
    if (tileAddRoot && tileAddRoot.addEventListener) {
      // CAPTURE, not bubble. Measured 2026-09-04: the bubbling version fired in
      // Trio and never in "Plates only" — something between the tile and the
      // section root stops propagation in that mode. Capturing on the root runs
      // before any of them, so the control cannot be silently disconnected by a
      // handler further in; it is also why the preventDefault below is enough to
      // keep the click off the tile's <label>.
      const onTileAddClick = (e) => {
        const btn = e.target && e.target.closest && e.target.closest('[data-nh-bb-tile-add]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.onTileAdd(btn);
      };
      const onCapAddClick = (e) => {
        const btn = e.target && e.target.closest && e.target.closest('[data-nh-bb-cap-add]');
        if (!btn || !this.toggleCapAdd) return;
        e.preventDefault();
        e.stopPropagation();
        this.toggleCapAdd(btn.getAttribute('data-nh-bb-cap-add'));
      };
      tileAddRoot.addEventListener('click', onCapAddClick, true);
      this.disposers.push(() => tileAddRoot.removeEventListener('click', onCapAddClick, true));
      tileAddRoot.addEventListener('click', onTileAddClick, true);
      this.disposers.push(() => tileAddRoot.removeEventListener('click', onTileAddClick, true));
    }
    this.tileAddBound = true;
  },

  paintTileAdd(tile, on) {
    const btn = tile.parentElement && tile.parentElement.querySelector('[data-nh-bb-tile-add]');
    if (!btn) return;
    const plate = this.plateAt(Number(tile.dataset.cat), Number(tile.dataset.index));
    const full = !on && this.chosen.length >= this.max;
    const soldOut = !!plate && (plate.variantId == null || plate.available === false);
    let text;
    if (on) {
      text = this.labels.inBundleRemove || 'In your bundle — remove';
    } else if (soldOut) {
      text = this.labels.soldOut || 'Sold out';
    } else if (full) {
      text = this.labels.allChosen
        ? fill(this.labels.allChosen, { total: this.max })
        : fill(this.labels.full || '', { total: this.max });
    } else if (plate && plate.name && this.labels.addPlate && this.labels.addPlate.includes('{name}')) {
      text = fill(this.labels.addPlate, { name: plate.name });
    } else {
      text = this.labels.addToBundle || 'Add to bundle';
    }
    btn.classList.toggle('is-chosen', on);
    const off = !on && (full || soldOut);
    btn.classList.toggle('is-off', off);
    btn.setAttribute('aria-disabled', String(off));
    const label = btn.querySelector('[data-nh-bb-tile-add-label]');
    if (label) label.textContent = text;
    // The visible word is short and fixed; the accessible name above is the one
    // that names the plate. Two spans, one control, so a pill can stay a pill.
    const word = btn.querySelector('[data-nh-bb-tile-add-text]');
    if (word) {
      word.textContent = on
        ? (this.labels.tileAdded || 'In bundle')
        : (this.labels.tileAdd || 'Add');
    }
  },

  /**
   * Click on a wall tile's add control. Toggle, not add-only: the same button
   * takes the plate back out, which is what makes the tick honest.
   *
   * Nothing here touches the stage. Choosing is not previewing — round 15.1's
   * finding, and the reason removeAt() does not repaint either: the picture
   * must not jump away from the plate the shopper is looking at.
   */
  onTileAdd(btn) {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const ci = Number(btn.dataset.cat);
    const pi = Number(btn.dataset.index);
    const at = this.indexOfChoice(ci, pi);
    if (at >= 0) {
      this.removeAt(at);
      return;
    }
    const plate = this.plateAt(ci, pi);
    if (!this.addPlate({ ci, pi })) return;
    this.afterChange();
    const name = plate && plate.name;
    const done = this.labels.addedConfirm || 'Added ✓';
    this.say(name ? `${done}, ${name}` : done, true);
  },

  /**
   * ROUND 22, PASS B — "Plates only" has no fixed offer, so its tray GROWS.
   *
   * `max` is the whole slot machinery's bound (renderTray, addPlate, syncBuy's
   * item loop), so rather than teach four places about an open-ended mode, the
   * mode keeps `max` one ahead of what is chosen: the tray shows the filled
   * tiles plus one empty, and adding is allowed until the ceiling. Sina picked
   * that ceiling — 12 — over an unbounded tray on 2026-09-04, so the form's
   * server-rendered rows stay the add path.
   */
  syncPlatesMax() {
    // The rows are shared with the caps (round 22.1): items[0] plus `slotCount`
    // rows carry everything posted, in order, so a tray of three caps has three
    // fewer rows for plates. Without this a 13th item was silently dropped from
    // the post while the button still counted it.
    const caps = this.capsInTray ? this.capsInTray().length : 0;
    const cap = this.trayCapacity ? this.trayCapacity() : ((this.slotCount || 12) + 1);
    const ceiling = Math.max(1, Math.min(this.slotCount || 1, cap - caps));
    this.max = Math.min(ceiling, this.chosen.length + 1);
    this.slots = this.allSlots.slice(0, this.max);
    this.confirms = this.allConfirms.slice(0, this.max);
    this.itemInputs = this.allItemInputs.slice(0, this.max + 1);
    this.allSlots.forEach((slot, i) => { slot.hidden = i >= this.max; });
    // The tray's column count is a variable someone has to write, and in this
    // mode `max` changes on every add — so it is written HERE, not only on a
    // mode switch. Without it the row kept whatever count the mode started with
    // (one), and each new plate stacked under the last instead of beside it.
    // Capped at 5: past that the tiles would shrink to keep one row, and a
    // twelve-plate tray is meant to wrap.
    if (this.syncTrayWidth) this.syncTrayWidth();
  },

  addPlate(ref) {
    if (this.chosen.length >= this.max) return false;
    if (this.indexOfChoice(ref.ci, ref.pi) >= 0) return false;
    const plate = this.plateAt(ref.ci, ref.pi);
    if (!plate || plate.variantId == null || plate.available === false) return false;
    this.chosen.push({ ci: ref.ci, pi: ref.pi });
    // ROUND 16 (serious finding, then item 11) — a pending "<plate> removed.
    // Undo" toast (up to 5s, sayUndo's sayTimer) otherwise outlived this add:
    // the still-visible Undo button offered nothing on a tray this add just
    // filled — its own guard refuses to re-insert past `this.max` — a silent
    // no-op. A fresh add supersedes the old removal's window; cancelling it
    // here dismisses the stale toast outright (it used to just let syncBuy's
    // hint recomputation paper over it — the toast is a separate element now
    // and nothing else will clear it).
    this.cancelUndo();
    return true;
  },

  /** Cancel a pending "<plate> removed. Undo" toast without acting on it —
   *  used when a fresh add supersedes it (see addPlate). Idempotent. */
  cancelUndo() {
    if (!this.sayTimer) return;
    clearTimeout(this.sayTimer);
    this.timers.delete(this.sayTimer);
    this.sayTimer = null;
    this.hideToast();
  },

  /** Hide the toast AND empty it. ROUND 17.1: a hidden toast used to keep its
   *  last text (and a dead Undo <button>) in the DOM — invisible, but the next
   *  reader of the tree (a test, a screen reader's virtual buffer after a
   *  refresh) saw "Added ✓, <plate>" for a plate that was gone. */
  hideToast() {
    const { toast, toastText } = this.els;
    if (toast) toast.hidden = true;
    if (toastText) toastText.textContent = '';
  },

  /**
   * Say what the tray is offering, or why it is not.
   *
   * Written into the hint line rather than a second live region: the hint is
   * where the shopper is already being told what to do next, and two regions
   * competing to speak is how double-announcements happen.
   */
  announceCandidate() {
    const full = this.chosen.length >= this.max && this.previewed
      && this.indexOfChoice(this.previewed.ci, this.previewed.pi) < 0;
    // ROUND 15: no full-tray flash in the Tray Sheet. The sheet's own button
    // carries the message, persistently and disabled, which is where the
    // shopper is looking; the hint under the bar keeps saying what is left.
    // ROUND 15.1: it is SPOKEN as well — through the status region, which is
    // in the bar and never inert — because a button that has just become
    // aria-disabled says nothing on its own.
    if (full && this.sheetActive()) {
      this.sayStatus(this.labels.allChosen
        ? fill(this.labels.allChosen, { total: this.max })
        : fill(this.labels.full || '', { total: this.max }));
      this.syncBuy();
      return;
    }
    if (full) {
      this.say(fill(this.labels.full, { total: this.max }), true);
      return;
    }
    // A stale "all chosen" line from the last preview is cleared, not re-read.
    if (this.statusText) this.sayStatus('');
    this.syncBuy();
  },

  /**
   * ROUND 15.1 — say something through the status region (`role="status"`,
   * in the buy block, which is the bar's footer on phones and therefore never
   * inert). The hint line is deliberately NOT live: it is rewritten on every
   * change with what is still missing, and the count region already announces
   * that — a live hint would read every add twice. This region carries the
   * moments the hint cannot: "Added ✓", "<plate> removed", "All 3 chosen".
   * The same text twice is re-announced by clearing first.
   */
  sayStatus(text) {
    const status = this.els.status;
    if (!status) return;
    this.statusText = text || '';
    if (!text) {
      status.textContent = '';
      return;
    }
    if (status.textContent === text) status.textContent = '';
    status.textContent = text;
  },

  afterChange() {
    // ROUND 24.1 — the tray's column count has to be re-derived on EVERY change,
    // not only in a growing tray. syncPlatesMax() is the only caller of
    // syncTrayWidth() on this path and it is gated on `trayGrows`, so in a fixed
    // bundle (Trio, Collector) the count kept whatever the mode switch set — and
    // a cap added afterwards had no column to go in, so it wrapped. The else
    // branch is a no-op on the bundle PDPs, which have no syncTrayWidth.
    if (this.trayGrows) this.syncPlatesMax();
    else if (this.syncTrayWidth) this.syncTrayWidth();
    this.renderTray();
    this.syncBuy();
    this.syncFullState();
    this.markPreviewed();
    this.markChosen();
    this.updateRungLinks();
    this.paintNudge();
    if (this.paintCapAdds) this.paintCapAdds();
    if (this.paintCapChips) this.paintCapChips();
    if (this.paintFreeTrayHead) this.paintFreeTrayHead();
    if (this.paintCapAdds) this.paintCapAdds();
    this.announceCount();
    this.clearAddStatus();
  },

  /**
   * ROUND 18.1 — WHERE THE WALL TILE'S TICK IS DRAWN, and therefore where its
   * accessible equivalent belongs. The bundle PDP paints it only inside the
   * phone Tray Sheet (`.nh-bb--sheet .nh-bb__tile.is-chosen ...`), so the
   * default is the sheet test it always was. A host whose stylesheet draws the
   * tick more widely overrides this and declares it in `owns`.
   */
  chosenMarkVisible() {
    return this.sheetActive();
  },

  /**
   * ROUND 15 — the wall tile's persistent tick. The tray used to be the single
   * record of what is being bought; in the Tray Sheet the tray is a parked fan
   * at the foot of the screen, so the wall gets a quiet second reading. A class
   * only: the mark is drawn by the stylesheet, inside the phone sheet branch,
   * so the desktop wall does not change.
   */
  markChosen() {
    // ROUND 15.1: the tick has an accessible equivalent. aria-pressed on a tile
    // means "on the stage", so the chosen state is a visually-hidden span at
    // the end of the button's name ("Bästkusten, in your bundle") — added and
    // removed with the class, and only where the tick is drawn (the sheet),
    // so the desktop wall's names are what they were.
    // ROUND 18.1 — ASK WHERE THE TICK IS DRAWN, do not assume the sheet.
    // `sheetActive()` is the bundle PDP's answer and stays its answer; Mix &
    // Match draws the same tick at EVERY viewport (its wall is 106 tiles long
    // and its tray is a strip inside the picture panel), so on a desktop
    // homepage the class was painted while the span was not written — state
    // conveyed by a graphic alone, WCAG 1.3.1 / 1.4.1. One question, two
    // answers, and the mark can no longer disagree with the CSS.
    this.bindTileAdd();
    const sheet = this.chosenMarkVisible();
    const text = this.labels.inBundle || 'In your bundle';
    this.previewButtons.forEach((el) => {
      const on = this.indexOfChoice(Number(el.dataset.cat), Number(el.dataset.index)) >= 0;
      el.classList.toggle('is-chosen', on);
      this.paintTileAdd(el, on);
      let mark = el.querySelector('[data-nh-bb-tile-chosen]');
      if (on && sheet) {
        if (!mark) {
          mark = document.createElement('span');
          mark.className = 'nh-bb__sr';
          mark.dataset.nhBbTileChosen = '';
          el.appendChild(mark);
        }
        mark.textContent = `, ${text}`;
      } else if (mark) {
        mark.remove();
      }
    });
  },

  /**
   * Carry the current state to the other rungs.
   *
   * The hrefs are rewritten, never built from scratch: the base is captured at
   * build time so an update appends to the product URL rather than to the
   * previous update's query string.
   */
  updateRungLinks() {
    const handles = this.chosen
      .map((c) => (this.plateAt(c.ci, c.pi) || {}).handle)
      .filter(Boolean);

    // The outro band's link. Same handoff as a `mix` rung — plate-snap restores
    // ONE plate, so only the first travels — but it is an <a> with a real href
    // rather than a listbox row, so the href itself is rewritten and the base
    // is kept in a data attribute. Appending to the live href would append to
    // the previous update's query string.
    (this.mixLinks || []).forEach((a) => {
      const base = a.dataset.nhBbMixlinkHref || '';
      if (!base) return;
      let url;
      try {
        url = new URL(base, window.location.origin);
      } catch {
        return;
      }
      if (this.capKey) url.searchParams.set('cap', this.capKey);
      if (handles.length) url.searchParams.set('plate', handles[0]);
      // ROUND 12: keep the fragment. The destination is the homepage's Mix &
      // Match BAND (`/#mix-and-match`) — there is no Mix & Match page — and
      // `pathname + search` drops the hash, landing the shopper at the top of
      // the homepage instead of on the band.
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });

    if (!this.rungs || !this.rungs.length) return;
    this.rungs.forEach((el) => {
      const base = el.dataset.nhBpHref || '';
      if (!base) return;
      let url;
      try {
        url = new URL(base, window.location.origin);
      } catch {
        return;
      }
      if (this.capKey) url.searchParams.set('cap', this.capKey);
      if (el.dataset.nhBpKind === 'mix') {
        // plate-snap restores ?plate=<handle>&cap=<key>, singular — it holds one
        // plate, not a set, so only the first travels.
        if (handles.length) url.searchParams.set('plate', handles[0]);
      } else if (handles.length) {
        url.searchParams.set('plates', handles.join(','));
      }
      el.dataset.nhBpTo = url.pathname + url.search + url.hash;
    });
  },

  syncFullState() {
    const full = this.chosen.length >= this.max;
    this.classList.toggle('nh-bb--full', full);
  },

  announceCount() {
    if (!this.els.count) return;
    this.els.count.textContent = fill(this.labels.count, {
      chosen: this.chosen.length,
      total: this.max,
    });
  },

  /**
   * Transient message in the toast above the bar/deck.
   *
   * ROUND 16 (Sina, item 11) — used to write into the hint line; the hint is
   * gone, and its persistent half moved onto the button's own label (see
   * syncBuy). The toast is what is left for a message that is not a standing
   * instruction: it floats above the bar (`position: absolute`), never
   * changes the button's or the bar's height, and clears itself. Not a
   * second live region — sayStatus() is what actually gets announced; this
   * is what gets SEEN.
   */
  say(message, transient) {
    const toast = this.els.toast;
    const toastText = this.els.toastText;
    if (!toast || !toastText) return;
    // A held-over Undo button from a previous toast belongs to a DIFFERENT
    // message; only sayUndo() puts one there.
    const stale = toastText.parentElement && toastText.parentElement.querySelector('.nh-bb__undo');
    if (stale) stale.remove();
    toastText.textContent = message;
    toast.hidden = false;
    if (!transient) return;
    if (this.sayTimer) {
      clearTimeout(this.sayTimer);
      this.timers.delete(this.sayTimer);
    }
    this.sayTimer = setTimeout(() => {
      this.timers.delete(this.sayTimer);
      this.sayTimer = null;
      this.hideToast();
    }, 3200);
    this.timers.add(this.sayTimer);
  },

  renderTray() {
    const candSlot = this.candidateSlot();
    const cand = this.candidate();
    const candPlate = cand ? this.plateAt(cand.ci, cand.pi) : null;
    // The first slot that is neither bought nor being offered — the one the "+"
    // stands on in the phone bar. -1 when the tray is full, and then no "+" is
    // drawn at all, which is the honest reading of "nothing left to choose".
    let firstEmpty = -1;
    for (let i = 0; i < this.slots.length; i += 1) {
      if (this.chosen[i]) continue;
      if (i === candSlot && candPlate) continue;
      firstEmpty = i;
      break;
    }

    this.slots.forEach((slot, i) => {
      const choice = this.chosen[i];
      const plate = choice ? this.plateAt(choice.ci, choice.pi) : null;
      const thumb = slot.querySelector('.nh-bb__slot-thumb');
      const name = slot.querySelector('.nh-bb__slot-name');
      const main = slot.querySelector('[data-nh-bb-slot-preview]');
      const confirmName = slot.querySelector('[data-nh-bb-confirm-name]');
      const confirm = slot.querySelector('[data-nh-bb-confirm]');
      const pre = slot.querySelector('[data-nh-bb-slot-pre]');
      // ROUND 12. The mark is drawn for a plate that IS in the tray and for the
      // candidate being offered into it, and cleared on an empty slot — same
      // three branches the rest of this loop has.
      const markPre = (pl) => {
        if (!pre) return;
        const on = !!(pl && pl.preorder);
        pre.textContent = on ? (this.labels.preorder || '') : '';
        slot.classList.toggle('is-preorder', on);
      };

      // ---- filled: a plate that is being bought --------------------------
      if (plate) {
        slot.classList.add('is-filled');
        slot.classList.remove('is-candidate', 'is-plus');
        if (main) main.disabled = false;
        // The tray thumbnail is the plate's own artwork, which is
        // cap-independent — so nothing in the tray repaints when a colourway
        // changes, on either axis.
        const src = plate.art || thumbSrc(plate, this.viewCap);
        if (thumb && src) thumb.style.setProperty('--nh-bb-slot-img', `url("${cssUrl(src)}")`);
        if (name) name.textContent = plate.name || '';
        // Round 9 removed the printed name, so the tooltip carries it for a
        // sighted mouse user — the accessible name (the hidden span above) was
        // always there and is unchanged.
        if (main) main.title = plate.name || '';
        // Ticked, and named for what unticking it does. The visible "Add" is
        // hidden by CSS on a filled tile, so the accessible name is this span
        // alone — which is why it carries the whole sentence rather than the
        // plate's name on its own.
        if (confirm) confirm.checked = true;
        markPre(plate);
        if (confirmName) {
          // The pre-order state belongs in the accessible name too — the visual
          // mark is aria-hidden, and this span IS the button's name.
          const base = fill(this.labels.remove, { name: plate.name || '' });
          confirmName.textContent = plate.preorder
            ? `${base} (${this.labels.preorder || ''})`
            : base;
        }
        // The row whose plate is on the stage right now.
        slot.classList.toggle('is-previewed',
          !!(this.previewed && choice.ci === this.previewed.ci && choice.pi === this.previewed.pi));
        return;
      }

      slot.classList.remove('is-filled', 'is-previewed');

      // ---- candidate: the previewed plate, offered here -------------------
      if (i === candSlot && candPlate) {
        slot.classList.add('is-candidate');
        // Cleared here as well as set below: this branch returns early, so a
        // slot that WAS the "+" and has just become the candidate would keep the
        // class and be drawn as both.
        slot.classList.remove('is-plus');
        if (main) main.disabled = false;
        const src = candPlate.art || thumbSrc(candPlate, this.viewCap);
        if (thumb && src) thumb.style.setProperty('--nh-bb-slot-img', `url("${cssUrl(src)}")`);
        if (name) name.textContent = candPlate.name || '';
        if (main) main.title = candPlate.name || '';
        if (confirm) confirm.checked = false;
        markPre(candPlate);
        // Leading space so the visible "Add" and the name read as one phrase —
        // and so the visible word stays a contiguous run at the head of the
        // accessible name, which is what WCAG 2.5.3 asks for.
        if (confirmName) confirmName.textContent = ` ${candPlate.name || ''}`;
        return;
      }

      // ---- empty ----------------------------------------------------------
      slot.classList.remove('is-candidate');
      markPre(null);
      if (thumb) thumb.style.removeProperty('--nh-bb-slot-img');
      if (confirm) confirm.checked = false;
      if (confirmName) confirmName.textContent = '';

      // THE "+" (round 11, Sina's phone mock). In the bar the empty slots
      // collapse into ONE dashed tile; this is the first of them, and it is the
      // one that shows. The rest are hidden by CSS in the compact layout only —
      // the desktop tray still draws every slot, which is what makes the count
      // readable there.
      //
      // It is not a dead tile: it carries the same sentence the hint does
      // ("Choose 2 more plates") as its accessible name, and pressing it takes
      // you to the wall those plates come from. A "+" that does nothing is worse
      // than no "+".
      const isPlus = i === firstEmpty;
      slot.classList.toggle('is-plus', isPlus);
      if (main) {
        main.disabled = !isPlus;
        if (isPlus) main.title = this.remainingLabel();
        else main.removeAttribute('title');
      }
      if (name) {
        name.textContent = isPlus
          ? this.remainingLabel()
          : fill(this.labels.slot, { index: i + 1 });
      }
    });
    // ROUND 15: the sheet's button reads the same state the tray just drew.
    this.syncSheetAdd();
  },

  /**
   * "Your plates — choose any 3". ROUND 18: shared, and read from a CAPTURED
   * node rather than `this.querySelector` — Mix & Match renders the tray
   * outside its own custom element, and on a phone both surfaces move it into
   * the dock. It is also re-run on every bundle switch there, because the
   * template is rendered once at the LARGEST bundle's count and the number in
   * it has to follow the bundle actually chosen.
   */
  paintTrayTitle() {
    const el = this.els.trayTitle;
    if (!el) return;
    const tpl = el.dataset.nhBbTrayTitle || el.textContent || '';
    el.dataset.nhBbTrayTitle = tpl;
    el.textContent = fill(tpl, { plates: this.max });
  },

  /**
   * "Choose 2 more plates" — the same sentence the hint under the button uses,
   * from the same two settings, so the "+" tile and the hint can never disagree
   * and neither holds any English of its own.
   *
   * The hint's own branch also covers "pick your cap", which does not belong on
   * a tile that is about plates; this is deliberately only the plate half.
   */
  remainingLabel() {
    const need = Math.max(0, this.max - this.chosen.length);
    if (need === 1) return this.labels.hintOne || '';
    if (need > 1) return fill(this.labels.hintMany, { count: need });
    return '';
  },

  /** Take a plate out of the tray. The stage is deliberately left alone. */
  removeAt(slotIndex) {
    if (!this.chosen[slotIndex]) return;
    const ref = this.chosen[slotIndex];
    this.chosen.splice(slotIndex, 1);
    // Repainting here made the picture jump away from the plate the shopper was
    // looking at, mid-decision. The stage shows what is PREVIEWED; removing
    // something from the tray says nothing about that.
    this.afterChange();
    // ROUND 15.1: in the Tray Sheet a removal offers Undo whichever control did
    // it — the sheet's button or the chosen tile's own checkbox. Focus goes to
    // the Undo: the box that was unticked is gone with its tile, and Undo is
    // the one thing the next five seconds are about.
    if (this.sheetActive()) {
      this.sayUndo(ref, slotIndex, { focus: true });
      return;
    }
    // Focus would otherwise fall to <body>: the tile that was unticked is now
    // empty and its control is hidden. The candidate's box is where the next
    // decision is; failing that, the last tile that still has one.
    const candSlot = this.candidateSlot();
    // From the cached list, not a query: the tray may be in the dock (round 15).
    const confirmAt = (n) => this.confirms.find((c) => c.dataset.nhBbConfirm === String(n)) || null;
    let next = candSlot >= 0 ? confirmAt(candSlot) : null;
    if (!next && this.chosen.length) next = confirmAt(this.chosen.length - 1);
    if (!next) next = this.els.button;
    if (next && !next.disabled) next.focus();
    // ROUND 17.1 (verifier, flow-and-cart): the desktop tray and the pinned
    // band said NOTHING when a filled tile was unticked — the plate vanished
    // with no confirmation and no way back, while the same removal from the
    // sheet offered "<plate> removed · Undo". One removal, one message, on
    // every surface. Focus stays where the rescue above put it (the next
    // checkbox); the Undo is reachable but not forced on a mouse user.
    this.sayUndo(ref, slotIndex, { focus: false });
  },

  /** The price as text, or '' on a host that does not compute one (the bundle
   *  PDP, whose button has always taken the bundle variant's own price). */
  freePriceText() {
    if (typeof this.freeTotal !== 'function' || typeof this.freeMoneyText !== 'function') return '';
    return this.freeMoneyText(this.freeTotal());
  },

  /**
   * ROUND 22 — THE NUDGE. One line, and only at a rung where the next plate is
   * cheaper than a plate (freeNudge() decides; twice in the whole ladder).
   *
   * Built here rather than in Liquid because it is a computed suggestion, not
   * content: there is nothing for a scriptless page to render. Inserted once,
   * ahead of the buy button, and hidden rather than removed so the node — and
   * its listener — survive every repaint.
   */
  paintNudge() {
    if (typeof this.freeNudge !== 'function') return;
    const host = this.els.buy || (this.els.button && this.els.button.parentElement);
    if (!host || !host.parentElement) return;
    let el = this.els.nudge;
    if (!el) {
      // ROUND 22.1 — NO BUTTON, on any screen. The desktop lost it because the
      // wall beside the panel already carries an Add on every tile; the phone
      // kept one that added `firstOfferedPlate()` — the first plate in the wall
      // the shopper had NOT chosen, i.e. a plate they never looked at. A nudge
      // that buys something on the shopper's behalf is not a nudge.
      //
      // ROUND 24 (Sina, 2026-09-07: "in the mobile version when u say 'add x
      // plates' if the user clicks the button, scroll to the plates"). The line
      // is now a real <button> that NAVIGATES — it takes the shopper to the
      // control that can do what the line suggests and puts focus on it. It
      // still never adds anything, so 22.1's rule holds: the objection there was
      // to a nudge that BOUGHT on the shopper's behalf, not to one that points.
      //
      // A button on every width, not just the phone. On desktop the panel is
      // sticky and the wall scrolls past it, so the tiles the line is talking
      // about are routinely off screen there too; and a control that exists only
      // under a media query is one the keyboard cannot reach at the other width.
      //
      // Only where the host can actually go somewhere: the bundle PDPs share
      // this core and have no wall, so there the nudge stays a plain line.
      el = document.createElement('div');
      el.className = 'nh-bb__nudge';
      el.hidden = true;
      const text = document.createElement('span');
      text.className = 'nh-bb__nudge-text';
      const goes = typeof this.revealWall === 'function';
      if (goes) {
        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'nh-bb__nudge-go';
        go.appendChild(text);
        // Decorative: the button's accessible name is the nudge sentence, which
        // is the whole point of putting the text inside it.
        const chev = document.createElement('span');
        chev.className = 'nh-bb__nudge-chev';
        chev.setAttribute('aria-hidden', 'true');
        chev.textContent = '\u2192';
        go.appendChild(chev);
        el.appendChild(go);
        this.on(go, 'click', () => this.nudgeGo());
      } else {
        el.appendChild(text);
      }
      // ROUND 22.2 (Sina: "should the green not be below the black?") — after
      // the buy block, not before it: the picks lead straight into the button
      // and the nudge reads as the footnote to it.
      host.parentElement.insertBefore(el, host.nextSibling);
      this.els.nudge = el;
      this.els.nudgeText = text;
    }
    const n = this.freeNudge();
    if (!n) { el.hidden = true; return; }
    const money = (v) => this.freeMoneyText(v);
    // Four shapes, one template each; a shape whose template is blank stays
    // silent rather than falling through to the wrong words.
    const L = this.labels;
    const tpl = n.kind === 'cap' ? L.nudgeCap
      : n.kind === 'free' ? L.nudgeFree
      : n.kind === 'less' ? L.nudgeLess
      : n.kind === 'two' ? (n.name ? L.nudgeTwo : L.nudgeTwoPlain)
      : (n.name && L.nudgeOneBundle) ? L.nudgeOneBundle
      : L.nudgeLine;
    if (!tpl) { el.hidden = true; return; }
    // What the button should reach for, read at click time. Stored per paint
    // because the shape changes as the tray does.
    this.nudgeKind = n.kind;
    this.els.nudgeText.textContent = fill(tpl, {
      price: money(n.next),
      name: n.name || '',
      delta: money(n.delta),
      each: money(n.each || 0),
      full: money(n.full),
    });
    el.hidden = false;
  },

  /**
   * ROUND 24 — where the nudge points. "Add a cap …" means the cap row; every
   * other shape is about plates and means the wall.
   *
   * The phone sheet is closed FIRST: it is the cap picture parked over the foot
   * of the viewport, and scrolling the page behind it lands the shopper on a
   * tile they cannot see. Closing is idempotent — setSheet() returns early when
   * the state already matches — and a no-op on desktop, where sheetOpen is
   * never true.
   */
  nudgeGo() {
    if (this.sheetOpen && typeof this.setSheet === 'function') this.setSheet(false);
    if (this.nudgeKind === 'cap' && typeof this.revealCaps === 'function') {
      this.revealCaps();
      return;
    }
    if (typeof this.revealWall === 'function') this.revealWall();
  },

  syncBuy() {
    // Hoisted: capOk below asks freeMode which kind of "picked" it means, and a
    // const used before its declaration is a TDZ throw, not a fallback.
    const platesMode = !!this.platesMode;
    const freeMode = !!this.freeMode;
    // capChosen(), not cap(): cap() falls back to the first colourway, which is
    // exactly the pre-selection round 7 removed.
    // ROUND 22 — in "Cap + plates" there is no bundle product, so capChosen()
    // (which reads the bundle's per-colourway variant) returns null by design.
    // The cap being bought there is the Art Cap PRODUCT itself, which the host
    // hands over as freeCap(). Everything downstream — variantId, price, the
    // sold-out branch — is unchanged.
    const cap = (this.freeMode && this.freeCap) ? this.freeCap() : this.capChosen();
    const { button, variant, label, price, name, wrapper } = this.els;
    if (!button) return;

    const need = this.max - this.chosen.length;
    const capPicked = !!cap;
    const capOk = (freeMode && this.capReallyChosen)
      ? this.capReallyChosen()
      : (capPicked && cap.variantId != null && cap.available !== false);

    // ROUND 22, PASS B — in "Plates only" items[0] is not bought at all: there
    // is no bundle variant and the cap is a separate decision. A disabled field
    // is dropped from FormData, so disabling the three items[0] inputs is what
    // makes this the same form posting only plate lines.

    // ROUND 22 — NO CAP IN THE TRAY IS PLATES-ONLY, not a blocked bundle. Once
    // the cap card's pill can take the cap out, "cap + plates with no cap" has
    // to price and post as plates alone; anything else is a dead end reachable
    // in one click.
    const platesLike = platesMode || (freeMode && !capOk);
    if (variant) {
      variant.value = (!platesLike && cap && cap.variantId != null) ? String(cap.variantId) : '';
      variant.disabled = platesLike;
    }
    if (this.els.variantQty) this.els.variantQty.disabled = platesLike;
    if (this.els.group) this.els.group.disabled = platesLike;

    // items[1..N]. A disabled field is dropped from FormData, so a partly filled
    // tray posts only the pairs that are real — and the button is disabled in
    // that state anyway, so this is belt and braces rather than a code path.
    // ROUND 22 — ONE COMBINED LIST when the host builds its own tray. The rows
    // stopped being "items[0] is the cap and 1..N are plates" the moment a tray
    // could hold several caps; trayItems() hands over what to post, in order,
    // and this writes it into whatever rows exist. The bundle PDP has no
    // trayItems() and keeps the original loop below, untouched.
    if ((platesLike || freeMode) && typeof this.trayItems === 'function') {
      const posted = this.trayItems();
      const rows = this.allItemInputs || [];
      let preCount2 = 0;
      // items[0] carries the first thing posted; the plate rows carry the rest.
      if (variant) {
        const head = posted[0];
        variant.value = head ? String(head.id) : '';
        variant.disabled = !head;
      }
      if (this.els.variantQty) {
        this.els.variantQty.disabled = !posted.length;
        // ROUND 23 — a posted item may carry a quantity (two refill packs are one
        // line, qty 2, not two rows: the form has 13 rows and 12 plates + 4 packs
        // would not fit).
        this.els.variantQty.value = String((posted[0] && posted[0].qty) || 1);
      }
      if (this.els.group) this.els.group.disabled = !posted.length;
      for (let i = 1; i < rows.length; i += 1) {
        const it = rows[i] || {};
        const item = posted[i];
        const ok = !!item;
        if (it.id) { it.id.value = ok ? String(item.id) : ''; it.id.disabled = !ok; }
        if (it.qty) { it.qty.disabled = !ok; it.qty.value = String((ok && item.qty) || 1); }
        if (it.grp) it.grp.disabled = !ok;
        if (it.part) {
          const want = ok && item.part;
          it.part.disabled = !want;
          if (want) it.part.value = item.part;
        }
        const isPre = !!(ok && item.pre);
        if (it.pre) it.pre.disabled = !isPre;
        if (isPre) preCount2 += 1;
      }
      this.setBundleGroup();
      if (this.els.prenote) {
        const note = preCount2 > 0 ? (this.labels.preorderNote || '') : '';
        this.els.prenote.textContent = note;
        this.els.prenote.hidden = !note;
      }
    } else {
    let preCount = 0;
    for (let i = 1; i <= this.max; i += 1) {
      const it = (this.itemInputs && this.itemInputs[i]) || {};
      const idInput = it.id;
      const qtyInput = it.qty;
      const grpInput = it.grp;
      const partInput = it.part;
      const preInput = it.pre;
      const choice = this.chosen[i - 1];
      const plate = choice ? this.plateAt(choice.ci, choice.pi) : null;
      const ok = !!(plate && plate.variantId != null && plate.available !== false);
      if (idInput) {
        idInput.value = ok ? String(plate.variantId) : '';
        idInput.disabled = !ok;
      }
      if (qtyInput) qtyInput.disabled = !ok;
      // ROUND 12. The two properties that bind this plate to the bundle travel
      // with the line or not at all — a disabled field is dropped from FormData,
      // so a half-filled tray can never post a "Part of: Trio" with no plate.
      if (grpInput) grpInput.disabled = !ok;
      if (partInput) partInput.disabled = !ok;
      // Mix & Match in bundle mode switches bundle WITHOUT a reload, so the
      // `Part of: <Bundle>` value cannot be the server-rendered one. The host
      // sets `partName` when it changes bundle; the bundle PDP never does, so
      // its server-rendered value stands exactly as it did.
      if (partInput && this.partName) partInput.value = this.partName;
      // `Sale type: Pre-order`, the same property the plate PDP attaches. Only
      // for a plate that actually is one.
      const isPre = !!(ok && plate.preorder);
      if (preInput) preInput.disabled = !isPre;
      if (isPre) preCount += 1;
    }

    // The group id binds every line of ONE add. It is refreshed here as well as
    // at submit time so a native (no-JS-cart) submit still carries one.
    this.setBundleGroup();

    if (this.els.prenote) {
      const note = preCount > 0 ? (this.labels.preorderNote || '') : '';
      this.els.prenote.textContent = note;
      this.els.prenote.hidden = !note;
    }
    }

    // The theme only honours the disabled ATTRIBUTE, never aria-disabled.
    // ROUND 22 — three shapes, not two. `free` keeps the cap on items[0] like a
    // bundle and grows its tray like plates-only, so it is blocked until BOTH
    // are present; `platesMode` never wants a cap; a bundle wants its full count.
    const blocked = platesLike ? this.chosen.length < 1
      : freeMode ? (!capOk || this.chosen.length < 1)
      : (!capOk || need > 0);
    const complete = platesLike ? this.chosen.length > 0
      : freeMode ? (capOk && this.chosen.length > 0)
      : (capOk && need === 0);
    button.disabled = blocked;
    if (wrapper) {
      // The theme's own flag for "leave this button disabled"; without it every
      // add-to-cart button on the page is re-enabled 1000ms later.
      wrapper.classList.toggle('variant--soldout', blocked);
    }

    // ROUND 16 (Sina, item 11) — THE HINT LINE IS GONE; the button's own
    // label now carries what it used to say underneath. Four states, in the
    // order a shopper clears them: no cap AND plates missing (one sentence —
    // two lines of instruction under a disabled button read as an error
    // report), no cap only, plates only, then — the only ENABLED state — the
    // name and price together, which used to be a separate span. Reusing the
    // same settings the hint always used (capPlaceholder / hintBoth(One) /
    // hintOne / hintMany) rather than inventing new copy: the wording did not
    // change, only where it is read.
    //
    // Prices, and price-shaped tokens, appear ONLY in the complete state —
    // `price` stays in the DOM permanently empty (`:empty` already hides it;
    // removing the span would mean re-deciding the button's accessible-name
    // span order for no gain).
    if (price) price.textContent = '';
    const capCount = (this.capsChosen || []).length;
    if (label && freeMode && capOk) {
      const n = this.chosen.length;
      if (!capOk) {
        label.textContent = this.labels.capPlaceholder || 'Pick your cap';
      } else if (n < 1) {
        label.textContent = this.labels.platesEmpty || 'Choose a plate';
      } else if (capCount > 1 && this.labels.freeCtaCaps) {
        // Two caps and one plate read "Add cap + 1 plate" before this branch —
        // the button describing a basket the shopper could see was wrong.
        label.textContent = fill(this.labels.freeCtaCaps, {
          caps: capCount, count: n, price: this.freePriceText(),
        });
      } else if (n === 1 && this.labels.freeCtaOne) {
        label.textContent = fill(this.labels.freeCtaOne, { price: this.freePriceText() });
      } else {
        label.textContent = fill(this.labels.freeCta || '', { count: n, price: this.freePriceText() });
      }
    } else if (label && platesLike) {
      const n = this.chosen.length;
      if (n < 1) {
        label.textContent = this.labels.platesEmpty || 'Choose a plate';
      } else if (n === 1 && this.labels.platesCtaOne) {
        label.textContent = fill(this.labels.platesCtaOne, { price: this.freePriceText() });
      } else {
        label.textContent = fill(this.labels.platesCta || '', { count: n, price: this.freePriceText() });
      }
    } else if (label) {
      if (capPicked && !capOk) {
        // "Sold out" belongs to a cap that IS picked and cannot be bought.
        label.textContent = this.labels.soldOut || 'Sold out';
      } else if (!capPicked && need > 0) {
        // ROUND 14 — a SINGULAR form. This branch is most of the phone flow
        // (shown whenever no cap is picked), and with one plate left it read
        // "Pick your cap and choose 1 plates" before hintBothOne existed.
        const both = (need === 1 && this.labels.hintBothOne)
          ? this.labels.hintBothOne
          : this.labels.hintBoth;
        label.textContent = both ? fill(both, { count: need }) : (this.labels.capPlaceholder || 'Pick your cap');
      } else if (!capPicked) {
        label.textContent = this.labels.capPlaceholder || 'Pick your cap';
      } else if (need === 1) {
        label.textContent = this.labels.hintOne || '';
      } else if (need > 1) {
        label.textContent = fill(this.labels.hintMany, { count: need });
      } else {
        label.textContent = fill(
          this.labels.addToCartPrice || this.labels.addToCart,
          { name: this.labels.name || '', price: (cap && cap.price) || '' }
        );
      }
    }
    if (name) {
      // Leading comma so it reads as a continuation, not a new sentence. Only
      // in the complete state now — appended to an instructional sentence
      // ("Choose 2 more plates, Björk Suede Classic") it read as noise the
      // other three states never had a reason to carry.
      name.textContent = complete && cap && cap.name ? `, ${cap.name}` : '';
    }

    this.announceCount();
    // The tray shape depends on chosen.length, so it is redrawn here rather
    // than only in afterChange — syncBuy is also what runs after the transient
    // "tray is full" message clears.
    this.renderTray();
  },

  /**
   * The per-add group id, written onto the bundle line and every plate line.
   *
   * A NEW id per add, so two Trios built from different plates are two groups
   * rather than one line of quantity 2 — which is also what stops Shopify
   * merging them, since it merges only lines whose variant AND properties match.
   *
   * The leading underscore is Shopify's convention for a property kept on the
   * order but hidden from the cart and checkout: the binding is machine-readable
   * without adding a row of noise the shopper has to read past.
   */
  setBundleGroup(fresh) {
    if (fresh || !this.bundleGroup) {
      const rand = Math.random().toString(36).slice(2, 8);
      this.bundleGroup = `${this.dataset.sectionId || 'nh'}-${Date.now().toString(36)}-${rand}`;
    }
    if (this.els.group) this.els.group.value = this.bundleGroup;
    for (let i = 1; i <= this.max; i += 1) {
      const grpInput = ((this.itemInputs && this.itemInputs[i]) || {}).grp;
      if (grpInput) grpInput.value = this.bundleGroup;
    }
  },

  onAddToCart(e) {
    const btn = this.els.button;
    if (!btn || btn.disabled) {
      e.preventDefault();
      return;
    }
    // A fresh group for this add, before anything reads the form — the native
    // submit path below returns without touching it.
    this.setBundleGroup(true);
    // Without the theme's cart JS, let the form submit natively rather than
    // swallowing the click.
    if (!window.theme || !window.theme.routes) return;

    e.preventDefault();
    // ROUND 18.1 — ONE DISCOUNTED CAP-AND-PLATES GROUP PER CART.
    //
    // Shopify applies ONE "Buy X get Y" automatic discount per order, and
    // every offer this store runs is one: `Art Cap` (buy a cap, get a plate),
    // `Trio` (buy the Trio, get 3) and `Collector` (get 5). Two of them in one
    // cart do not stack — the winner's `customerGets` eats the loser's plates
    // and the shopper is silently OVERCHARGED at checkout. Measured on the
    // real cart, 2026-09-03:
    //
    //   Trio + Collector      270 500  where 224 000 is right   (+465 kr)
    //   cap+plate + Trio      185 500  where 169 000 is right   (+165 kr)
    //
    // It is a discount-configuration limit, not a theme bug — `combinesWith
    // .productDiscounts` is already true on all three — and no theme file can
    // make two BXGY discounts stack. What a theme CAN do is refuse to build
    // the cart that triggers it, which is what this does: the add is held, the
    // shopper is told which line is in the way and sent to the cart. A probe
    // that fails for any reason resolves to null and the add goes through, so
    // a slow or blocked /cart.js can never stop a sale.
    this.cartClash().then((clash) => {
      if (clash) { this.reportCartClash(clash); return; }
      this.submitBundleAdd();
    });
  },

  /**
   * The bundle product handle this section is about to add. plate-snap sets
   * `this.bundle` when it switches; the bundle PDP carries its own handle in
   * the payload's settings. One expression covers both surfaces, so there is
   * no override to keep in step.
   */
  cartBundleHandle() {
    return (this.bundle && this.bundle.handle) || (this.settings && this.settings.handle) || '';
  },

  /**
   * Variant ids that already carry a BXGY offer of their own — the eight Art
   * Caps. The host supplies them (plate-snap reads them straight off the caps
   * it already renders); a surface that has none returns an empty list and
   * only the bundle-versus-bundle half of the guard runs.
   */
  cartClashVariantIds() {
    return ((this.data && this.data.caps) || []).map((c) => c.capVariantId).filter((v) => v != null);
  },

  cartClash() {
    const msg = this.labels.cartClash;
    const mine = this.cartBundleHandle();
    if (!msg) return Promise.resolve(null);
    let capIds = null;
    try {
      const ids = this.cartClashVariantIds() || [];
      capIds = new Set(ids.filter((v) => v != null).map(String));
    } catch (err) { capIds = new Set(); }
    const root = String((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
    const url = `${root.replace(/\/$/, '')}/cart.js`;
    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return null;
        // The bundle's HEAD line is the one carrying `_nh_bundle` and no
        // `Part of` — its plate lines carry both, and a loose plate carries
        // neither. Two Trios in one cart are fine (one discount, two uses,
        // measured 199 000), so only a DIFFERENT bundle is a clash.
        const head = cart.items.find((it) => {
          const pr = it.properties || {};
          return pr._nh_bundle && !pr['Part of'] && it.handle && it.handle !== mine;
        });
        if (head) return { title: head.product_title || head.handle, kind: 'bundle' };
        if (!capIds || !capIds.size) return null;
        const cap = cart.items.find((it) => capIds.has(String(it.variant_id)));
        return cap ? { title: cap.product_title || cap.handle, kind: 'cap' } : null;
      })
      .catch(() => null);
  },

  reportCartClash(clash) {
    const msg = fill(this.labels.cartClash, { name: clash.title || '' });
    // The toast is where every other refusal on this surface is read, and it
    // is inside the dock on a phone, so it survives the sheet being open.
    this.say(msg, true);
    this.sayStatus(msg);
    // ROUND 22.2 (Sina: "when i click this button, the website's cart should
    // open"). A refused add used to leave the shopper on a button that did
    // nothing visible but a toast at the foot of the screen. The line in the
    // way is IN THE CART, so open the cart: the drawer shows it, its × removes
    // it, and the toast is still there when the drawer closes.
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.openCartDrawer === 'function') {
      try { drawer.openCartDrawer(true); } catch (err) { /* the toast stands */ }
    }
  },

  submitBundleAdd() {
    const btn = this.els.button;
    if (!btn) return;
    // ROUND 16 (a11y BLOCKER). The cart drawer (group-overlay) opens in a
    // section that setPageInert's upward walk from the dock can catch — while
    // the sheet was open, the drawer was born inside an inert ancestor, so
    // NONE of its controls were focusable or tappable ("Close Cart" included).
    // Releasing the sheet FIRST undoes that inert mark synchronously
    // (setPageInert(false) runs inside setSheet before this returns), so by
    // the time theme:cart:add's listener opens the drawer nothing outside the
    // dock is inert any more. Safe when the sheet was already parked, or in
    // `band` mode, where setSheet no-ops without `.dock`/`.sheet`.
    this.setSheet(false, { instant: true });
    // The theme restores focus here when the cart drawer closes.
    window.a11y = window.a11y || {};
    window.a11y.lastElement = btn;
    document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button: btn } }));
  },

  announceAdd(ok, event) {
    const { status, error } = this.els;
    if (ok) {
      if (error) error.textContent = '';
      if (status) status.textContent = this.labels.added || 'Added to your cart.';
      return;
    }
    if (status) status.textContent = '';
    if (!error) return;
    const res = event && event.detail && event.detail.response;
    error.textContent =
      (res && (res.description || res.message)) ||
      this.labels.error ||
      'Sorry, that could not be added to your cart.';
  },

  clearAddStatus() {
    if (this.els.status) this.els.status.textContent = '';
    if (this.els.error) this.els.error.textContent = '';
  },

  /**
   * SIZE THE BAR'S TILES FROM THE ROOM THERE ACTUALLY IS (round 11, Sina: "there
   * is spare width to the right of the fanned plates - use it").
   *
   * A fixed 60px tile left Collector's row 29px short of the inset and Trio's
   * 117px short: one tile size cannot be right for a three-plate bundle and a
   * five-plate one at once. The width is solved from the bar's MEASURED content
   * box instead, so each bundle fills its row and Trio's tiles come out larger
   * than Collector's.
   *
   * Only the WIDTH moves. The height stays the row height, which is what keeps
   * the bar's height fixed - and it suits the artwork, which is 16:9: a Trio tile
   * lands near 109x60 on a 375 phone, close to the plate's own shape.
   *
   * TWO STATES COMPETE FOR THE WIDEST ROW, and which one wins depends on the
   * tile width itself, so both are solved and the smaller answer is taken:
   *
   *   a) N-1 chosen plus the "+"   H + 2g + P + W[1 + (N-2)(1-k)]
   *   b) all N chosen, no "+"      H +  g      + W[1 + (N-1)(1-k)]
   *
   * (b) - (a) is W(1-k) - g - P, so (b) wins for a wide tile and (a) for a narrow
   * one. Solving only (a) - which is what the old fixed-tile row happened to be
   * bounded by - gives Trio a 125px tile whose three-tile full state is 362px in
   * a 343px row. Worked out after getting it wrong once.
   *
   * `k` is the overlap as a FRACTION of the tile, so the fan reads the same at
   * every size instead of the overlap eating a narrow tile alive.
   */
  sizeBarTiles() {
    const bar = this.els.bar;
    const root = this.bcRoot();
    if (!bar || bar.hidden || !this.mq || !this.mq.matches) return;
    const cs = getComputedStyle(bar);
    const inset = bar.clientWidth
      - (parseFloat(cs.paddingLeft) || 0)
      - (parseFloat(cs.paddingRight) || 0);
    if (!(inset > 0)) return;

    const H = 60;    // the row's height, and the cap tile's side. Fixed.
    const g = 8;     // cap -> stack, and stack -> "+"
    const P = 44;    // the "+" tile
    const k = 0.27;  // overlap, as a fraction of the tile's width
    const N = Math.max(1, this.max);

    // ROUND 15.1: THE ROW IS THE TRAY'S MEASURED BOX, not the bar's less an
    // estimate. The estimate (bar inset − cap − 8 − 6) missed the grid's 10px
    // column gap and the tray's own trailing padding + spacer, and the
    // Collector's five tiles overflowed their rail by exactly those 13px at
    // 390. The tray is a `minmax(0, 1fr)` track, so its width does not depend
    // on the tiles it holds and can be read before they are sized. The old
    // arithmetic stays as the fallback for a tray that has no box yet.
    const tray = this.els.tray;
    let row = inset - 6 - H - g;
    if (tray && tray.clientWidth > 0) {
      const ts = getComputedStyle(tray);
      row = tray.clientWidth
        - (parseFloat(ts.paddingLeft) || 0)
        - (parseFloat(ts.paddingRight) || 0)
        - 1;   // the ::after spacer
    }
    if (!(row > 0)) return;

    const wa = (row - g - P) / (1 + Math.max(0, N - 2) * (1 - k));
    const wb = row / (1 + (N - 1) * (1 - k));
    // Clamped so a one-plate bundle cannot produce a tile the width of the
    // screen, and a very narrow phone cannot produce a sliver.
    const W = Math.max(40, Math.min(2 * H, Math.min(wa, wb)));

    root.style.setProperty('--nh-bb-bar-w', `${W.toFixed(1)}px`);
    root.style.setProperty('--nh-bb-bar-overlap', `${(W * k).toFixed(1)}px`);
  },

  /**
   * ROUND 15.1 — everything that is not the dock is inert while the sheet is
   * open. The scrim already blocks a finger; a keyboard walked straight past
   * it (Tab from the tile landed on the compare slider under the scrim). The
   * dock's siblings, and its ancestors' siblings up to <body>, are marked and
   * remembered, so exactly those are released — the same walk a dialog
   * polyfill makes. Idempotent in both directions.
   */
  setPageInert(on) {
    const dock = this.els && this.els.dock;
    if (!dock) return;
    if (on) {
      if (this.inerted) return;
      const skip = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE']);
      const marked = [];
      let node = dock;
      while (node && node !== document.body) {
        const parent = node.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach((sib) => {
          if (sib === node || sib.inert || skip.has(sib.tagName)) return;
          sib.inert = true;
          marked.push(sib);
        });
        node = parent;
      }
      this.inerted = marked;
      return;
    }
    (this.inerted || []).forEach((el) => { el.inert = false; });
    this.inerted = null;
  },

  /** Listeners the sheet needs, bound once, the first time it becomes active.
   *  Never bound on a page that stays above 901px. */
  bindSheet() {
    const { dock, sheet, sheetGrip, scrim, sheetAdd, stage } = this.els;
    if (!dock || !sheet) return;
    this.sheetBound = true;

    // The dock holds the bar (tray, checkboxes, cap tile) and the strip (its
    // chips), and it is outside this element — so the delegated handlers are
    // bound here too. See bind().
    if (this.onDelegatedClick) this.on(dock, 'click', this.onDelegatedClick);
    if (this.onDelegatedChange) this.on(dock, 'change', this.onDelegatedChange);

    if (sheetGrip) {
      this.on(sheetGrip, 'click', () => this.setSheet(!this.sheetOpen));
      this.bindSheetDrag(sheetGrip, sheet);
    }
    // Scrim tap and Escape are the two extra exits. pointerdown on the scrim
    // would race nothing — it is its own element — so a plain click is enough.
    if (scrim) this.on(scrim, 'click', () => this.setSheet(false));
    if (sheetAdd) this.on(sheetAdd, 'click', (e) => this.onSheetAdd(e));

    dock.querySelectorAll('[data-nh-bb-step]').forEach((b) => {
      this.on(b, 'click', () => this.stepPlate(Number(b.dataset.nhBbStep) || 1));
    });
    this.on(document, 'keydown', (e) => {
      // The info card's own Escape runs first (it is bound closer to the
      // target) and prevents default, so one Escape closes one thing.
      if (e.key !== 'Escape' || !this.sheetOpen || e.defaultPrevented) return;
      e.preventDefault();
      this.setSheet(false);
    });
    // The host may already own a horizontal drag on this very stage (Mix &
    // Match steps the COLOURWAY with one). Two handlers on one element both
    // fire, so a host that has its own declares `bcStageSwipe = false`.
    if (stage && this.bcStageSwipe !== false) this.bindStageSwipe(stage);

    // Two triggers on purpose (plate-snap's note): matchMedia('change') is the
    // correct one and was observed not firing on a programmatic viewport
    // change; the failure mode strands the stage in a hidden box. A debounced
    // resize is the safety net — placeSheet() is idempotent.
    let t = null;
    const onResize = () => {
      clearTimeout(t);
      this.timers.delete(t);
      t = setTimeout(() => {
        this.timers.delete(t);
        this.placeSheet();
        this.parkSheet();
      }, 120);
      this.timers.add(t);
    };
    this.on(window, 'resize', onResize);
    this.on(window, 'orientationchange', onResize);

    this.bindSheetAway();
  },

  /**
   * THE SHEET LETS GO AT THE BUILDER'S END — exactly where the sticky bar
   * released. `position: fixed` releases nothing on its own (README round 11),
   * so the end sentinel is watched: once it is inside or above the viewport the
   * whole dock slides off the bottom, and it comes back when the sentinel drops
   * below the viewport again. The sentinel carries the peek's height as a
   * margin in sheet mode, so the wall's last row clears the parked bar before
   * the bar goes.
   */
  bindSheetAway() {
    const end = this.els.endSentinel;
    if (!end) return;
    // ROUND 15.1 — THE DOCK RELEASES LIKE THE STICKY BAR IT REPLACES, not by a
    // threshold. The end sentinel sits `--nh-bb-peek-h` (grip + bar) below the
    // wall's last row: that margin IS the bar's in-flow seat. While the
    // sentinel is below the viewport the dock is fixed at 0; once it rises
    // past the bottom edge the dock moves UP by the same distance, so the peek
    // sits exactly in its reserved seat under the last row and scrolls away
    // with the page — the sticky bar's own behaviour, and what "releases at the
    // builder's end" always meant. The first cut sent the whole dock off the
    // BOTTOM the moment the sentinel appeared: the last row was still fully on
    // screen and tappable with no bar under it, an add from that row parked the
    // sheet and then slid the receipt off-screen, and the 194px seat showed as
    // blank space.
    //
    // Measured on every scroll frame rather than trusted to an intersection
    // change: a zero-height sentinel crossing the viewport in one jump (End,
    // an anchor, a programmatic scroll) produces NO change of intersection,
    // and the IO alone left the dock stranded on both sides (measured). The IO
    // stays as the trigger for a layout change that moves the sentinel
    // without a scroll.
    if (this.awayIo) this.awayIo.disconnect();
    if ('IntersectionObserver' in window) {
      this.awayIo = new IntersectionObserver(() => this.applyAway(), { threshold: 0 });
      this.awayIo.observe(end);
    }
    // No rAF coalescing: the browser already fires `scroll` at most once per
    // frame, and the work is one rect read and one property write.
    // ROUND 21 — one listener for both releases, so a scroll frame still costs
    // one handler; applySectionAway() adds one rect read beside applyAway()'s.
    this.on(window, 'scroll', () => { this.applySectionAway(); this.applyAway(); }, { passive: true });
    this.bindSectionAway();
    this.applyAway();
  },

  /* ----------------------------------------------------------------------
     ROUND 21, ITEM H — THE BAR GOES AWAY WITH THE SECTION, BOTH WAYS.

     `applyAway()` above releases the dock at the END SENTINEL, which is the
     section's own foot: it slides the peek into its reserved seat under the
     wall's last row and lets it scroll off with the page. That is right for the
     bottom edge and says NOTHING about the top one — measured on the dev
     homepage at 375x812, section 3137..9622:

       scrollY      0  dock visible (foot 672..812) — 3137px ABOVE the section
       scrollY    500  visible
       scrollY   2737  visible
       scrollY   9722  foot -70..70, sliding (100px past the section's end)
       scrollY  10522  away

     So a shopper who has not reached Mix & Match, or who has just passed it,
     gets a fixed buy bar over another section — which is what the owner
     photographed over "The Art Plate, up close".

     AN INTERSECTION OBSERVER ON THE SECTION, not scroll arithmetic: the target
     is a 6 485px box, so an in/out change cannot be jumped over the way round
     15.1's zero-height sentinel could. The scroll listener above is kept as the
     belt for a layout change that moves the section without an intersection
     event, and it costs one rect read.

     `display`, not `[hidden]`: CLAUDE.md records `[hidden]` losing to any
     class-level `display` in this codebase, and `.nh-bb__dock` has one.

     THE SHEET IS PARKED BEFORE THE DOCK GOES, and never while an add is in
     flight — round 16's rule is that the sheet is parked BEFORE
     `theme:cart:add` is dispatched, so a bar that hid mid-add would leave the
     cart drawer inert. While `sheetAdding` this returns and the next scroll
     frame (or the IO) re-decides.
     ---------------------------------------------------------------------- */

  bindSectionAway() {
    if (this.sectionIo) this.sectionIo.disconnect();
    if ('IntersectionObserver' in window) {
      this.sectionIo = new IntersectionObserver(() => this.applySectionAway(), { threshold: 0 });
      this.sectionIo.observe(this);
      this.disposers.push(() => { if (this.sectionIo) this.sectionIo.disconnect(); });
    }
    this.applySectionAway();
  },

  applySectionAway() {
    const dock = this.els.dock;
    if (!dock || !this.sheetActive || !this.sheetActive()) return;
    // An add owns the sheet until it has dispatched; leave the state alone.
    if (this.sheetAdding) return;
    const r = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const off = r.bottom <= 0 || r.top >= vh;
    if (off === this.dockOffSection) return;
    this.dockOffSection = off;
    if (off) {
      if (this.sheetOpen) this.setSheet(false);
      dock.classList.add('is-offsection');
      return;
    }
    dock.classList.remove('is-offsection');
    // Coming back from `display: none` the sheet's box was 0x0 while it was
    // gone, so anything measured from it is stale. Re-seat it, and let the host
    // re-measure whatever it derives from the stage (round 15's rule that the
    // parked sheet must keep a real box is what this is paying back).
    if (this.placeSheet) this.placeSheet();
    if (this.parkSheet) this.parkSheet();
    if (typeof this.syncStageBleed === 'function') this.syncStageBleed();
    this.applyAway();
  },

  applyAway() {
    const { dock, endSentinel: end } = this.els;
    if (!dock || dock.hidden || !end || !this.sheetActive()) return;
    // An open sheet stays where the shopper opened it: at the foot of the
    // viewport. Re-evaluated on park, through the settling transition.
    if (this.sheetOpen) {
      dock.style.setProperty('--nh-bb-away', '0px');
      dock.classList.remove('is-away');
      dock.inert = false;
      this.sheetPastEnd = false;
      return;
    }
    const vh = dock.clientHeight || window.innerHeight;
    const top = end.getBoundingClientRect().top;
    // 0 while the seat is below the viewport; negative — up — by exactly how
    // far it has risen past the bottom edge; never more than the dock's own
    // height, past which nothing of it is on screen anyway.
    const y = Math.round(clamp(top - vh, -vh, 0));
    dock.style.setProperty('--nh-bb-away', `${y}px`);
    this.sheetPastEnd = y < 0;
    // Fully off the top: a state flag for the tests and the README, no rule —
    // ROUND 16 (a11y, serious). It now DOES do one thing: `inert` while fully
    // off-screen, because the grip, the bar's cap tile, the fan buttons and
    // their tick checkboxes stayed in the Tab order and tappable-by-coordinate
    // while translated past the top edge. Only the FULLY-away state is inert —
    // the partial slide (the peek still sitting in its reserved seat under the
    // wall's last row) is still on screen and still the working receipt.
    const away = y <= -vh;
    dock.classList.toggle('is-away', away);
    dock.inert = away;
  },

  /**
   * The dock's geometry, in pixels the JS wrote.
   *
   *   --nh-bb-bar-h    the bar's measured height: the sheet's floor
   *   --nh-bb-sheet-h  the sheet's height, viewport minus the bar. Anchored to
   *                    the bar's top and sized by this number rather than
   *                    stretched to the dock's top, so mobile browser chrome
   *                    showing or hiding moves the sheet WITH the bar instead
   *                    of opening a gap between the grip and the bar.
   *   --nh-bb-peek-h   grip + bar: what the parked sheet costs the page, and
   *                    what the end sentinel is pushed down by.
   *
   * Re-run whenever the bar resizes (ResizeObserver) and on the resize net.
   */
  syncDockGeometry({ instant = false } = {}) {
    const { dock, sheet, sheetGrip, bar } = this.els;
    const root = this.bcRoot();
    if (!dock || dock.hidden || !sheet) return;
    const barH = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    const gripH = sheetGrip ? Math.round(sheetGrip.getBoundingClientRect().height) : 0;
    const dockH = Math.round(dock.getBoundingClientRect().height);
    dock.style.setProperty('--nh-bb-bar-h', `${barH}px`);
    dock.style.setProperty('--nh-bb-sheet-h', `${Math.max(0, dockH - barH)}px`);
    root.style.setProperty('--nh-bb-peek-h', `${gripH + barH}px`);
    this.parkSheet({ instant });
  },

  /**
   * Recompute where the sheet parks, in pixels (plate-snap's parkSheet).
   *
   * A pixel, never calc(100% - x): 100% resolves against the sheet's own
   * height, and that re-resolves whenever mobile browser chrome shows or hides
   * — the transition's target kept moving and never settled, and a transition
   * stuck in `running` outranks even an inline style. A pixel only changes
   * when the JS says so.
   */
  parkSheet({ instant = false } = {}) {
    const sheet = this.els.sheet;
    if (!sheet || !this.sheetActive()) return;
    const y = Math.max(0, sheet.offsetHeight - this.peekHeight());
    if (instant) sheet.classList.add('is-dragging');
    sheet.style.setProperty('--nh-bb-parked', `${y}px`);
    if (instant) {
      void sheet.offsetWidth;
      sheet.classList.remove('is-dragging');
    }
  },

  /** How much of the SHEET stays on screen when parked: the grip. The bar is
   *  outside the sheet, in the dock's foot, and is always on screen. */
  peekHeight() {
    const grip = this.els.sheetGrip;
    const h = grip ? grip.offsetHeight : 0;
    return h > 0 ? h : 30;   // round 17: grip is 12 + 6 + 12
  },

  /**
   * Open or park the sheet. The body is `inert` while parked so nothing
   * off-screen can take focus or be read; focus that was inside it goes back
   * to the previewed tile, which is the thing the shopper was acting on.
   */
  setSheet(open, { instant = false } = {}) {
    const { sheet, sheetGrip, sheetBody, dock, sheetAdd } = this.els;
    if (!sheet || !dock) return;
    const want = !!open && this.sheetActive();
    const was = !!this.sheetOpen;
    // ROUND 16 (perf) — THE STRIP'S CHIP IMAGES ARE DEFERRED UNTIL THE FIRST
    // OPEN, same lever as the cap grid's `list.hidden` above: a laid-out box
    // fetches its CSS background regardless of visibility, so the parked sheet
    // was fetching ~13 chip images (~290KB) nobody could see. renderStrip()
    // keeps the strip forced `hidden` until `sheetEverOpened` is set; re-run
    // it once, here, so the images that are ABOUT to be seen start loading.
    if (want && !this.sheetEverOpened) {
      this.sheetEverOpened = true;
      this.renderStrip();
    }
    // A park during the "Added ✓" quarter-second settles it first: the label
    // must never be left reading "Added ✓" on a parked sheet.
    if (!want) this.settleAdded();
    this.parkSheet({ instant });
    this.sheetOpen = want;
    if (instant) sheet.classList.add('is-dragging');
    sheet.classList.toggle('is-open', want);
    dock.classList.toggle('is-open', want);
    if (instant) {
      void sheet.offsetWidth;
      sheet.classList.remove('is-dragging');
    }
    // ROUND 15.1: the dock's own move (the release, see applyAway) is
    // transitioned only while the sheet is opening or parking, so the two
    // motions settle together; on a scroll frame it follows the finger.
    if (want !== was && !instant) {
      dock.classList.add('is-settling');
      if (this.settleTimer) {
        clearTimeout(this.settleTimer);
        this.timers.delete(this.settleTimer);
      }
      this.settleTimer = setTimeout(() => {
        this.timers.delete(this.settleTimer);
        this.settleTimer = null;
        dock.classList.remove('is-settling');
      }, 340);
      this.timers.add(this.settleTimer);
    }
    if (sheetBody) {
      sheetBody.inert = !want;
      // Opening lands on the picture, whatever the body was scrolled to last.
      if (want && !was) sheetBody.scrollTop = 0;
    }
    // The cap grid's thumbnails are fetched on the first open, not on load.
    if (want && this.els.capList) this.els.capList.hidden = false;
    if (sheetGrip) {
      sheetGrip.setAttribute('aria-expanded', String(want));
      sheetGrip.setAttribute('aria-label',
        want ? this.labels.sheetClose || 'Hide the cap' : this.labels.sheetOpen || 'Show the cap');
    }
    // The bar's cap tile opens this sheet; its expanded state is the sheet's.
    if (this.els.capBtn && this.sheetActive()) {
      this.els.capBtn.setAttribute('aria-expanded', String(want));
    }
    // ROUND 15.1 — A MODAL, FOR A KEYBOARD TOO. The page outside the dock is
    // inert while the sheet is open, and focus MOVES INTO the sheet, onto the
    // one button under the picture — the primary action, whose name carries
    // the plate ("Add to bundle, Bästkusten"). Before, focus stayed on the tile
    // behind the scrim and the next Tab walked the page under it.
    if (want && !was) {
      this.setPageInert(true);
      if (sheetAdd) sheetAdd.focus({ preventScroll: true });
    }
    if (!want && was) {
      // Released BEFORE focus goes back, or the tile cannot take it.
      this.setPageInert(false);
      const active = document.activeElement;
      if (active && (active === document.body || (sheetBody && sheetBody.contains(active)))) {
        const tile = this.tileFor(this.previewed);
        if (tile) tile.focus({ preventScroll: true });
        else if (sheetGrip) sheetGrip.focus({ preventScroll: true });
      }
    }
    if (!want) this.setPageInert(false);
    this.applyAway();
  },

  /** The wall tile for a plate reference, or null. */
  tileFor(ref) {
    if (!ref) return null;
    return this.previewButtons.find(
      (el) => Number(el.dataset.cat) === ref.ci && Number(el.dataset.index) === ref.pi
    ) || null;
  },

  /**
   * Drag the handle (plate-snap's bindSheetDrag, verbatim). Commits on
   * DISTANCE past a threshold rather than on release velocity: a slow
   * deliberate pull should behave the same as a flick, and velocity punishes
   * the former.
   */
  bindSheetDrag(grip, sheet, onTap) {
    let y0 = null;
    let wasOpen = false;
    let moved = false;

    // A drag that ends past the threshold commits the new state — and then the
    // browser fires `click` on the same element, which toggled it straight back.
    // Swallow the click that follows a real drag; a tap (no movement) still
    // falls through to the handler.
    this.on(grip, 'click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      moved = false;
    }, true);

    this.on(grip, 'pointerdown', (e) => {
      y0 = e.clientY;
      moved = false;
      wasOpen = sheet.classList.contains('is-open');
      sheet.classList.add('is-dragging');
      if (grip.setPointerCapture) grip.setPointerCapture(e.pointerId);
    });
    this.on(grip, 'pointermove', (e) => {
      if (y0 === null) return;
      const dy = e.clientY - y0;
      if (Math.abs(dy) > 4) moved = true;
      // The sheet is placed by transform, so the finger can be tracked in BOTH
      // directions. Clamped to the range the two resting positions occupy so
      // it can never be dragged off its own track.
      const parked = sheet.offsetHeight - this.peekHeight();
      const base = wasOpen ? 0 : parked;
      const next = Math.max(0, Math.min(base + dy, parked));
      // Inline transform during the drag: it outranks both resting rules.
      sheet.style.transform = `translateY(${next}px)`;
    });
    const finish = (e) => {
      if (y0 === null) return;
      const dy = e.clientY - y0;
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
      if (Math.abs(dy) > 40) this.setSheet(dy < 0);
      else if (!moved && onTap) onTap();
      else this.setSheet(wasOpen);
      y0 = null;
    };
    this.on(grip, 'pointerup', finish);
    this.on(grip, 'pointercancel', () => {
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
      y0 = null;
    });
  },

  /**
   * Horizontal drag on the stage steps the plate (plate-snap's
   * bindStageSwipe: 45px, and clearly more sideways than up — on a phone the
   * stage fills the sheet, so an ambiguous drag must stay a scroll).
   * Bound only once the sheet has been active; the step itself is gated too,
   * so the desktop stage never moves.
   */
  bindStageSwipe(stage) {
    let x0 = null, y0 = null, id = null;
    const THRESHOLD = 45;

    this.on(stage, 'pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      x0 = e.clientX; y0 = e.clientY; id = e.pointerId;
    });
    const end = (e) => {
      if (x0 == null || e.pointerId !== id) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = y0 = id = null;
      if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.stepPlate(dx < 0 ? 1 : -1);
    };
    this.on(stage, 'pointerup', end);
    this.on(stage, 'pointercancel', () => { x0 = y0 = id = null; });
  },

  /**
   * The previous / next plate in the previewed plate's category, wrapping,
   * over the tiles that are actually shown for this colourway. Sheet only.
   */
  stepPlate(delta) {
    if (!this.sheetActive() || !this.previewed) return;
    this.settleAdded();
    const { ci, pi } = this.previewed;
    const panel = this.catPanels[ci];
    if (!panel) return;
    const tiles = Array.from(panel.querySelectorAll('[data-nh-bb-preview]')).filter(
      (el) => !el.closest('.nh-bb__cell').hidden
    );
    if (tiles.length < 2) return;
    const at = tiles.findIndex((el) => Number(el.dataset.index) === pi);
    if (at < 0) return;
    const next = tiles[((at + delta) % tiles.length + tiles.length) % tiles.length];
    this.preview({ ci, pi: Number(next.dataset.index) }, { animate: true });
  },

  /**
   * ONE button under the picture, and its label is the state:
   *   add     — the previewed plate is offered:  "Add to bundle"
   *   remove  — it is already in the tray:      "In your bundle — remove"
   *   full    — the tray is full, disabled:     "All 3 chosen — remove one to swap"
   *   soldout — it cannot be bought, disabled
   * Called from renderTray, which runs on every change that could matter.
   */
  syncSheetAdd() {
    const btn = this.els.sheetAdd;
    if (!btn) return;
    if (this.sheetAdding) return;   // the "Added ✓" moment owns the label
    const label = this.els.sheetAddLabel;
    const nameEl = this.els.sheetAddName;
    const plate = this.previewedPlate();
    const cur = this.previewed;
    const idx = cur ? this.indexOfChoice(cur.ci, cur.pi) : -1;
    let text = this.labels.addToBundle || 'Add to bundle';
    let disabled = true;
    let mode = 'add';
    // ROUND 17 (Sina, item 1): the offer names the plate — "Add Bästkusten".
    // The stylesheet cuts a long name with an ellipsis so it stays one line;
    // an empty template or a nameless plate falls back to the plain label.
    let named = false;
    if (plate && plate.name && this.labels.addPlate && this.labels.addPlate.includes('{name}')) {
      text = fill(this.labels.addPlate, { name: plate.name });
      named = true;
    }
    if (!plate) {
      disabled = true;
    } else if (idx >= 0) {
      text = this.labels.inBundleRemove || 'In your bundle — remove';
      disabled = false;
      mode = 'remove';
    } else if (this.chosen.length >= this.max) {
      text = this.labels.allChosen
        ? fill(this.labels.allChosen, { total: this.max })
        : fill(this.labels.full || '', { total: this.max });
      mode = 'full';
    } else if (plate.variantId == null || plate.available === false) {
      text = this.labels.soldOut || 'Sold out';
      mode = 'soldout';
    } else if (this.candidate()) {
      disabled = false;
    }
    if (label) label.textContent = text;
    // ROUND 15.1: aria-disabled, not disabled. A disabled button leaves the Tab
    // order and takes focus with it — the full-tray state was unreachable and
    // unannounced, and "Added ✓" dropped focus to <body> for its 250ms. The
    // click handler honours the attribute; the stylesheet dims on it.
    btn.disabled = false;
    btn.setAttribute('aria-disabled', String(disabled));
    btn.dataset.nhBbMode = mode;
    // Leading comma so it reads as a continuation of the visible text. Not
    // appended when the visible label already carries the name (round 17):
    // "Add Bästkusten, Bästkusten" would say it twice.
    const sayName = plate && plate.name && !(mode === 'add' && named);
    if (nameEl) nameEl.textContent = sayName ? `, ${plate.name}` : '';
  },

  onSheetAdd(e) {
    const btn = this.els.sheetAdd;
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (this.sheetAdding) return;
    const mode = btn.dataset.nhBbMode;
    // A click a keyboard raised (Enter / Space) has detail 0; a pointer's has
    // its click count. Only a keyboard user needs focus carried to the Undo.
    const keyboard = !!e && e.detail === 0;

    if (mode === 'remove') {
      const cur = this.previewed;
      const idx = cur ? this.indexOfChoice(cur.ci, cur.pi) : -1;
      if (idx < 0) return;
      const ref = this.chosen[idx];
      this.chosen.splice(idx, 1);
      // Not removeAt(): that moves focus to a tray checkbox, and the shopper's
      // finger is on THIS button, which now reads "Add to bundle" again.
      this.afterChange();
      this.sayUndo(ref, idx, { focus: keyboard });
      return;
    }

    if (mode !== 'add') return;
    const cand = this.candidate();
    const plate = cand ? this.plateAt(cand.ci, cand.pi) : null;
    if (!cand || !this.addPlate(cand)) return;
    // "ADDED ✓" for a quarter of a second while the ghost in the fan turns
    // solid (renderTray does that immediately), then the sheet parks. The flag
    // is raised BEFORE afterChange so syncSheetAdd leaves the label alone.
    this.sheetAdding = true;
    this.afterChange();
    if (this.els.sheetAddLabel) {
      this.els.sheetAddLabel.textContent = this.labels.addedConfirm || 'Added ✓';
    }
    btn.classList.add('is-added');
    // aria-disabled, so focus stays on the button for the quarter-second and
    // the park below can hand it to the tile (README round 15.1).
    btn.setAttribute('aria-disabled', 'true');
    // Spoken: the count region says "1 of 3 chosen"; this says what was added.
    this.sayStatus(`${this.labels.addedConfirm || 'Added ✓'}, ${(plate && plate.name) || ''}`);
    this.addedTimer = setTimeout(() => {
      this.timers.delete(this.addedTimer);
      this.addedTimer = null;
      this.sheetAdding = false;
      btn.classList.remove('is-added');
      this.syncSheetAdd();
      this.setSheet(false);
    }, 250);
    this.timers.add(this.addedTimer);
  },

  /**
   * ROUND 15.1 — end the "Added ✓" moment early, without parking. A tap on
   * another tile, a step, or a park during the quarter-second used to run
   * under the confirmation: the stage repainted while the button still read
   * "Added ✓", and then the timer parked the sheet on the plate the shopper
   * had just asked to see. Idempotent; a no-op outside the window.
   */
  settleAdded() {
    if (!this.sheetAdding) return;
    if (this.addedTimer) {
      clearTimeout(this.addedTimer);
      this.timers.delete(this.addedTimer);
      this.addedTimer = null;
    }
    this.sheetAdding = false;
    const btn = this.els.sheetAdd;
    if (btn) btn.classList.remove('is-added');
    this.syncSheetAdd();
  },

  /**
   * "<Plate> removed · UNDO" in a toast above the bar/deck for five seconds.
   * Undo puts the plate back in the slot it came from. Uses the toast's own
   * transient timer (sayTimer).
   *
   * ROUND 16 (Sina, item 11) — used to write into the hint line, which is
   * gone; the toast is its replacement (see say()). The Undo button is a
   * normal, focusable, keyboard-reachable control living inside the toast —
   * not a second live region: sayStatus() below is still what is announced.
   */
  sayUndo(ref, idx, { focus = false } = {}) {
    const toast = this.els.toast;
    const toastText = this.els.toastText;
    const plate = this.plateAt(ref.ci, ref.pi);
    if (!toast || !toastText || !plate) return;
    if (this.sayTimer) {
      clearTimeout(this.sayTimer);
      this.timers.delete(this.sayTimer);
      this.sayTimer = null;
    }
    const removed = fill(this.labels.removed || '{name} removed', { name: plate.name || '' });
    toastText.textContent = `${removed} `;
    const undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'nh-bb__undo';
    undo.textContent = this.labels.undo || 'Undo';
    toastText.appendChild(undo);
    toast.hidden = false;
    // ROUND 15.1: spoken through the status region (the toast is not live, on
    // purpose — see sayStatus), with the Undo named so the listener knows the
    // five seconds exist.
    this.sayStatus(`${removed}. ${this.labels.undo || 'Undo'}`);
    // Where focus goes when the Undo goes: the sheet's button if the sheet is
    // up (it reads "Add to bundle" for this very plate), else the plate's
    // wall tile. Never <body>, which is where it fell before.
    const land = () => {
      const active = document.activeElement;
      if (active !== undo && active !== document.body) return;
      if (this.sheetOpen && this.els.sheetAdd) {
        this.els.sheetAdd.focus({ preventScroll: true });
        return;
      }
      // ROUND 17.1: on the desktop tray / pinned band (sayUndo now runs there
      // too) the plate's own checkbox is the control the Undo restored — land
      // on it, else on the candidate's box; the wall tile is the last resort.
      if (!this.sheetActive()) {
        const at = this.indexOfChoice(ref.ci, ref.pi);
        const n = at >= 0 ? at : this.candidateSlot();
        const box = n >= 0
          ? this.confirms.find((c) => c.dataset.nhBbConfirm === String(n))
          : null;
        if (box && !box.disabled && !box.hidden) {
          box.focus({ preventScroll: true });
          return;
        }
      }
      const tile = this.tileFor(ref);
      if (tile) tile.focus({ preventScroll: true });
    };
    const done = (expired) => {
      if (this.sayTimer) {
        clearTimeout(this.sayTimer);
        this.timers.delete(this.sayTimer);
        this.sayTimer = null;
      }
      const held = document.activeElement === undo;
      // hideToast() empties the text too, which removes this very Undo button
      // from the DOM — `held` is read first, and land() below re-homes focus.
      this.hideToast();
      this.syncBuy();
      // ROUND 16 (minor finding) — the status region kept offering "<plate>
      // removed. Undo" after the five seconds actually lapsed: sayStatus is
      // only ever called from sayUndo, and nothing had ever cleared it once
      // the window closed on its own, so a user who re-read the region later
      // was told an Undo existed that was already gone. Only the NATURAL
      // timeout clears it — a click already gets its own fresh state via
      // syncBuy()/afterChange(), and clearing the region out from under an
      // in-progress screen-reader announcement of it would be worse.
      if (expired) this.sayStatus('');
      if (held) land();
    };
    undo.addEventListener('click', () => {
      if (this.chosen.length < this.max && this.indexOfChoice(ref.ci, ref.pi) < 0) {
        this.chosen.splice(Math.min(idx, this.chosen.length), 0, { ci: ref.ci, pi: ref.pi });
      }
      done();
      this.afterChange();
      land();
    }, { once: true });
    this.sayTimer = setTimeout(() => done(true), 5000);
    this.timers.add(this.sayTimer);
    if (focus) undo.focus({ preventScroll: true });
  },

  /* ======================================================== the dock frame */

  /**
   * The generic half of "put the phone dock in or take it out again".
   *
   * The NODES that move differ per surface — the builder moves its stage,
   * caption row, gallery strip and buy bar; Mix & Match moves its whole
   * `.nh-ps__media` and its buy bar — so that half is the host's
   * `bcSheetMove(active)`. Everything around it is one copy: the root's
   * `nh-bb--sheet` class, the dock's `hidden`, the one-time bindSheet, the
   * first-placement park WITHOUT a transition (the fallback transform is
   * 100%, and a cold load would otherwise slide the grip up), the body's
   * `inert` while parked, and the re-derive of the clip now that the stage's
   * box has moved.
   */
  placeDock() {
    const { dock, sheet, sheetBody } = this.els;
    const root = this.bcRoot();
    if (!dock || !sheet || !sheetBody) return;
    const active = this.sheetActive();

    if (active) {
      // The band and the sheet never coexist; setCompactStage refuses to turn
      // the band ON while the sheet is active, so this can only ever restore.
      if (this.compact) this.setCompactStage(false);
      root.classList.add('nh-bb--sheet');
      this.bcSheetMove(true);
      const first = dock.hidden;
      dock.hidden = false;
      if (!this.sheetBound) this.bindSheet();
      this.syncDockGeometry({ instant: first });
      sheetBody.inert = !this.sheetOpen;
      this.paintGeometry();
      this.syncSheetAdd();
      this.syncCapRoving();
      this.markChosen();
      return;
    }

    const had = root.classList.contains('nh-bb--sheet') || !dock.hidden;
    if (!had) return;
    this.setSheet(false, { instant: true });
    root.classList.remove('nh-bb--sheet');
    dock.hidden = true;
    this.bcSheetMove(false);
    this.paintGeometry();
    this.syncCapRoving();
    this.markChosen();
  },

  /* ======================================================================
     ROUND 19 — THE PANEL'S REMAINING SHARED HALF

     Round 18 shared the ordered set, the tray, the buy form and the phone
     Tray Sheet. Round 19's brief was that Mix & Match's left column must
     PRESENT exactly like the bundle page's panel in every mode, so the four
     things still private to the builder came here too: the cap control (a
     dropdown on desktop, a centred <dialog> chooser on phones), the info
     popover, the caption row's painter, and the gallery strip.

     Everything below is a MOVE, not a rewrite. Every body is the builder's,
     comment for comment; `this` is still the custom element, and the only
     edits are the commas an object literal needs where a class body did not.

     The one thing here that was not simply moved is paintCapButton(), and it
     is not new behaviour either — only the DOM half of the builder's
     selectCap(), lifted out so a host whose colourway state lives elsewhere
     can paint the same button without owning the same state.
     ====================================================================== */

  /**
   * Paint the cap control from a cap object.
   *
   * Why it had to come out of selectCap(): on the bundle PDP `capKey` IS the
   * state and selectCap() sets it. On Mix & Match the state is a native radio
   * group — eight <input type="radio"> that drive applyCap, the ?cap= sync and
   * the roving tabindex — and round 19's rule for that surface is that the
   * dropdown CHOOSES BY CLICKING THE REAL RADIO, so the existing, measured path
   * runs unchanged and no second source of truth can disagree with it. That host
   * needs the painting without the state-setting; this is the painting, and the
   * builder calls it so its own behaviour is byte-for-byte what it was.
   */
  paintCapButton(cap) {
    if (!cap) return;
    // The dropdown is the control now, so its button and its rows carry the
    // state that the radio group used to.
    const capList = this.els.capList || this.els.capCombo;
    if (capList) {
      capList.querySelectorAll('[data-nh-bb-capopt]').forEach((opt) => {
        opt.setAttribute('aria-selected', String(opt.dataset.nhBbCapopt === cap.key));
      });
      const chosenOpt = capList.querySelector(`[data-nh-bb-capopt="${cssAttr(cap.key)}"]`);
      const thumb = chosenOpt && chosenOpt.querySelector('.nh-bb__combo-thumb');
      if (thumb && this.els.capBtnThumb) {
        // The real photograph replaces the drawing, and the placeholder class
        // goes with it — the drawing is contained, the photograph covers.
        this.els.capBtnThumb.classList.remove('nh-bb__combo-thumb--placeholder');
        this.els.capBtnThumb.style.setProperty(
          '--nh-bb-combo-img', thumb.style.getPropertyValue('--nh-bb-combo-img'));
      }
    }
    if (this.els.capName) {
      // "Your cap – Faluröd Suede Trucker" (Sina's Figma, round 8): the label
      // moved into the button, so the button has to say what it is naming. The
      // prefix travels in a data attribute rather than in the JS, because it is
      // a theme setting with its own Swedish.
      const prefix = (this.els.capName.dataset.nhBbCapPrefix || '').trim();
      this.els.capName.textContent = prefix
        ? `${prefix} – ${cap.name || ''}`
        : (cap.name || '');
    }
    this.capAffix(true);
  },

  /**
   * The dropdown's right-hand word: "Choose" while nothing is picked, "Change"
   * once something is (Sina's Figma, round 9). Both words are theme settings
   * carried on the element, so the Swedish is registered like any other string
   * and this function never holds English.
   */
  capAffix(picked) {
    const el = this.els.capAffix;
    if (!el) return;
    const word = picked ? el.dataset.nhBbAffixChange : el.dataset.nhBbAffixChoose;
    if (word) el.textContent = word;
    this.syncCapTileLabel();
  },

  /**
   * The phone bar's cap TILE has no visible text, so it needs a name of its own
   * (round 11, Sina).
   *
   * On desktop the button is named by `aria-labelledby` — the hidden label plus
   * the visible "Your cap – Faluröd Suede Trucker" line. That is left exactly as
   * approved. In the bar the visible line is not rendered at all, so the tile
   * takes an `aria-label` instead, composed from the same three settings the
   * button already carries: the caps label, the cap's name and the Change/Choose
   * word. `aria-labelledby` is removed while that is in force, because it wins
   * over `aria-label` and the name would silently stay the desktop one.
   *
   * No English lives here: every part comes off the DOM, so the Swedish is
   * whatever those settings are registered as.
   */
  syncCapTileLabel() {
    const btn = this.els.capCombo && this.els.capCombo.querySelector('[data-nh-bb-combo-btn]');
    if (!btn) return;
    const tile = this.els.capCombo.classList.contains('nh-bb__combo--tile');
    if (!tile) {
      if (btn.dataset.nhBbLabelledby) {
        btn.setAttribute('aria-labelledby', btn.dataset.nhBbLabelledby);
        delete btn.dataset.nhBbLabelledby;
      }
      btn.removeAttribute('aria-label');
      return;
    }
    const name = this.els.capName ? this.els.capName.textContent.trim() : '';
    const affix = this.els.capAffix ? this.els.capAffix.textContent.trim() : '';
    const label = affix ? `${name}, ${affix}` : name;
    if (!label) return;
    if (!btn.dataset.nhBbLabelledby && btn.getAttribute('aria-labelledby')) {
      btn.dataset.nhBbLabelledby = btn.getAttribute('aria-labelledby');
    }
    btn.removeAttribute('aria-labelledby');
    btn.setAttribute('aria-label', label);
  },
  /* ------------------------------------------------------------- combobox */

  /**
   * One listbox behaviour for both dropdowns — the bundle switcher and the cap
   * selector. They differ only in what activating a row does, which is decided
   * by the row's own data attributes, so there is one keyboard contract and one
   * open/close path rather than two that drift.
   *
   * The contract is the native one, because that is what people already know:
   * Up/Down/Home/End move, Enter or Space choose, Escape closes and returns
   * focus to the button. Focus moves into the list on open so the arrow keys
   * have something to act on; `tabindex="-1"` on the rows keeps them out of the
   * page's tab order, where eleven of them would be eleven stops.
   */
  bindCombos() {
    this.combos.forEach((combo) => {
      const btn = combo.querySelector('[data-nh-bb-combo-btn]');
      const list = this.comboList(combo);
      if (!btn || !list) return;

      this.on(btn, 'click', () => {
        // ROUND 15.1: in the Tray Sheet the cap tile OPENS, never toggles —
        // its aria-expanded mirrors the sheet now, and a second tap while the
        // sheet is up must bring the cap block into view, not close anything.
        const open = combo === this.els.capCombo && this.sheetActive()
          ? true
          : !this.comboOpen(combo);
        this.toggleCombo(combo, open);
      });
      this.on(btn, 'keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.toggleCombo(combo, true);
        }
      });

      this.on(list, 'click', (e) => {
        const opt = e.target.closest('[role="option"]');
        if (opt) this.chooseOption(combo, opt);
      });
      this.on(list, 'keydown', (e) => this.onComboKeydown(e, combo));
    });

    // The phone's cap modal. `showModal()` gives Escape, the ::backdrop and the
    // focus trap; the three things it does not give are a backdrop CLICK, a
    // visible close control, and returning focus to the tile — all three below.
    const dlg = this.els.capModal;
    const combo = this.els.capCombo;
    if (dlg && combo) {
      const capBtn = combo.querySelector('[data-nh-bb-combo-btn]');
      const shut = () => {
        this.toggleCombo(combo, false);
        if (capBtn) capBtn.focus();
      };
      // A click that lands on the <dialog> itself is a click on the backdrop:
      // everything visible is in a child, so the element's own box is only
      // reachable outside them.
      this.on(dlg, 'click', (e) => { if (e.target === dlg) shut(); });
      // Escape. preventDefault first, or the dialog closes itself and the
      // listbox is left inside it for the desktop layout to find.
      this.on(dlg, 'cancel', (e) => { e.preventDefault(); shut(); });
      const x = dlg.querySelector('[data-nh-bb-capmodal-close]');
      if (x) this.on(x, 'click', shut);
    }

    // A click anywhere else closes whatever is open. Bound once on the document
    // rather than per combo, and torn down with the rest in disconnectedCallback.
    const away = (e) => {
      this.combos.forEach((combo) => {
        if (this.comboOpen(combo) && !combo.contains(e.target)) this.toggleCombo(combo, false);
      });
    };
    this.on(document, 'pointerdown', away);
  },

  /* ------------------------------------------------------- info popover */

  /**
   * "About this plate" — the circled i on the caption row.
   *
   * The button exists only for a plate that HAS a description (`hasDesc` in the
   * payload); the description itself is fetched from the plate's own `<url>.js`
   * the first time it is wanted, and cached per handle. 101 descriptions inline
   * would be ~57 KB on a page whose payload is already this section's known
   * cost, and almost nobody opens them — so the flag travels and the body does
   * not.
   *
   * Hover and keyboard focus PREFETCH, so the common case is already in the
   * cache by the time the button is pressed. Nothing is fetched for a shopper
   * who never goes near it.
   */
  bindInfo() {
    const btn = this.els.info;
    const pop = this.els.pop;
    if (!btn || !pop) return;

    this.on(btn, 'click', () => this.toggleInfo(!this.infoOpen));
    const warm = () => {
      // ROUND 21 — the subject, not the plate: in cap view there is no plate and
      // the cap's body is already inlined, so loadInfo() caches it and fetches
      // nothing. `description` is tested beside `hasDesc` for the same reason
      // round 19 added it to syncInfo — Mix & Match's payload carries one and
      // the bundle PDP's carries the other.
      const subject = this.infoSubject();
      if (subject && (subject.hasDesc || subject.description)) this.loadInfo(subject);
    };
    this.on(btn, 'pointerenter', warm);
    this.on(btn, 'focus', warm);

    // The visible close control. Same contract as Escape — it closes and hands
    // focus back to the button that opened it, which is directly above it.
    const x = pop.querySelector('[data-nh-bb-pop-close]');
    if (x) this.on(x, 'click', () => this.toggleInfo(false));

    // Escape closes and hands focus back, from the button or from inside the
    // card — the same contract the listboxes keep.
    this.on(this.els.capline || pop, 'keydown', (e) => {
      if (e.key !== 'Escape' || !this.infoOpen) return;
      e.preventDefault();
      this.toggleInfo(false);
    });

    // A click anywhere else dismisses it. pointerdown, not click, so it closes
    // on the press rather than after the release — and bound on the document
    // beside the listboxes' own dismissal, torn down with the rest.
    this.on(document, 'pointerdown', (e) => {
      if (!this.infoOpen) return;
      if (btn.contains(e.target) || pop.contains(e.target)) return;
      this.toggleInfo(false, { focus: false });
    });

    // Tabbing out of the card is a dismissal too, and must not steal focus back.
    this.on(pop, 'focusout', (e) => {
      if (!this.infoOpen) return;
      const to = e.relatedTarget;
      if (to && (pop.contains(to) || btn.contains(to))) return;
      this.toggleInfo(false, { focus: false });
    });

    // The card is anchored to a row inside a height-bounded, scrolling panel, so
    // which side it opens on is a function of the room in THAT box, not the
    // viewport. Re-measured on resize; the panel's own scrolling does not move
    // the anchor relative to the card, because both live inside it.
    this.on(window, 'resize', () => { if (this.infoOpen) this.placeInfo(); });
  },

  /** Show or hide the card. `focus` returns focus to the button on close. */
  toggleInfo(open, { focus = true } = {}) {
    const btn = this.els.info;
    const pop = this.els.pop;
    if (!btn || !pop) return;
    if (open && btn.hidden) return;

    this.infoOpen = !!open;
    btn.setAttribute('aria-expanded', String(!!open));
    pop.hidden = !open;

    if (!open) {
      if (focus) btn.focus();
      return;
    }
    const plate = this.infoSubject();
    if (!plate) {
      this.infoOpen = false;
      pop.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      return;
    }
    this.fillInfo(plate);
    this.placeInfo();
    this.loadInfo(plate);
  },

  /**
   * The button's presence and its name, kept in step with the previewed plate.
   *
   * Called from paintCapNote, which already runs on every change that could
   * matter. The open card is dismissed when the PLATE changes — it is about a
   * particular plate — but not when the colourway does, because the card says
   * nothing about the cap.
   */
  syncInfo(plate) {
    const btn = this.els.info;
    if (!btn) return;
    const handle = plate ? plate.handle || '' : '';
    if (handle !== this.infoHandle) {
      if (this.infoOpen) this.toggleInfo(false, { focus: false });
      this.infoHandle = handle;
    }
    // ROUND 19 — `hasDesc` OR an inlined `description`.
    //
    // nordenheim-plate-json emits ONE of the two, never both: `with_description`
    // true inlines the body (which Mix & Match asks for, because its accordion
    // renders the description on the page) and the flag is emitted only in the
    // other branch. So the bundle PDP's plates carry `hasDesc` and Mix & Match's
    // carry `description` — and testing only the flag hid the info button on
    // every plate on the homepage, which is the one control the caption row is a
    // ROW for. Measured: 0 of 91 plates offered it.
    const show = !!(plate && (plate.hasDesc || plate.description));
    btn.hidden = !show;
    if (!show) return;
    if (this.els.infoName) {
      // ROUND 21 — a cap gets its own wording ("About the Timmer Suede Trucker"
      // is a different sentence from "About Bästkusten", and its Swedish is a
      // different sentence again). The attribute is absent on the bundle PDPs,
      // which have no cap view, so they fall through to the one they had.
      const tpl = (plate.isCap && btn.dataset.nhBbInfoCapLabel)
        || btn.dataset.nhBbInfoLabel
        || '';
      // Both tokens are offered so the template decides which it uses; a cap
      // template written with {plate} would still read correctly.
      this.els.infoName.textContent = tpl
        ? fill(tpl, { plate: plate.name || '', cap: plate.name || '' })
        : plate.name || '';
    }
  },

  /** Everything the card can say from the payload alone. */
  fillInfo(plate) {
    if (this.els.popTitle) this.els.popTitle.textContent = plate.name || '';
    const collab = this.els.popCollab;
    if (collab) {
      const line = [plate.collab || '', plate.collabAmount || ''].filter(Boolean).join(' · ');
      collab.textContent = line;
      collab.hidden = !line;
    }
    const link = this.els.popLink;
    if (link) {
      if (plate.url) link.setAttribute('href', plate.url);
      // ROUND 21 — "View this Art Plate" / "View this Art Cap". Both live in
      // data attributes rather than one being the element's shipped text: once
      // the text can change, reading the current text to restore it is reading
      // whatever the previous subject left behind.
      const word = (plate.isCap && link.dataset.nhBbPopLinkCap)
        || link.dataset.nhBbPopLinkPlate
        || '';
      if (word) link.textContent = word;
    }
    this.paintInfoBody(plate.handle);
  },

  /** The description, from the cache. Empty until the fetch lands. */
  paintInfoBody(handle) {
    const body = this.els.popBody;
    if (!body) return;
    const html = (this.descCache && this.descCache.get(handle)) || '';
    // The merchant's own product description, from their own store, rendered the
    // way the PDP renders it. Same trust boundary as {{ product.description }}.
    body.innerHTML = html;
    body.hidden = !html;
  },

  /**
   * Fetch one plate's description. `<url>.js` is Shopify's own product JSON and
   * `url` is already locale-prefixed, so /sv/ gets the Swedish body with no
   * second code path. ~1.5 KB over the wire, once per plate.
   */
  loadInfo(plate) {
    const key = plate && plate.handle;
    if (!key) return;
    this.descCache = this.descCache || new Map();
    // ROUND 19 — a payload that already carries the body needs no request. Mix &
    // Match inlines every description (its accordion renders one), so the fetch
    // below would be ~1.5 KB asking the store for text already in the page.
    if (plate.description && !this.descCache.has(key)) {
      this.descCache.set(key, plate.description);
    }
    if (!plate.url && !this.descCache.has(key)) return;
    this.descPending = this.descPending || new Set();
    if (this.descCache.has(key)) {
      if (this.infoOpen && this.infoHandle === key) this.paintInfoBody(key);
      return;
    }
    if (this.descPending.has(key)) return;
    this.descPending.add(key);
    const done = (html) => {
      this.descCache.set(key, html || '');
      this.descPending.delete(key);
      // Only paint if this is still the plate on screen and the card is still
      // open — a slow response must never overwrite a newer one.
      if (this.infoOpen && this.infoHandle === key) {
        this.paintInfoBody(key);
        this.placeInfo();
      }
    };
    fetch(`${plate.url}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => done(d && d.description))
      // A failed fetch caches an empty body rather than retrying on every open:
      // the card still names the plate and still links to it, which is the part
      // that matters, and the link is where the description lives anyway.
      .catch(() => done(''));
  },

  /**
   * Which side the card opens on, and how tall it may be.
   *
   * The bound is the SCROLL CONTAINER, not the window: on desktop the panel is
   * height-capped with `overflow-y: auto`, and round 8 records the cap listbox
   * being cut off for exactly this reason. Below 901px the panel is not bounded
   * and the viewport is the bound instead.
   *
   * Downward by default — the caption sits under the picture, so down is where
   * the room is. Up only when down cannot hold a readable card and up can hold
   * more, in which case the card opens over the picture.
   *
   * On a phone the floor is the STICKY BUY BAR, not the bottom of the window.
   * The bar is fixed to the viewport's bottom edge and is 140px tall, so a card
   * measured against the window opens underneath it and the description is read
   * by nobody. Measured at 375x812: 86px of real room below the caption against
   * 516px above, so it opens upward over the picture.
   */
  placeInfo() {
    const pop = this.els.pop;
    const row = this.els.capline;
    if (!pop || !row) return;
    const panel = this.els.panel;
    const bounded = panel && getComputedStyle(panel).overflowY !== 'visible';
    let box;
    const sheetBody = this.els.sheetBody;
    if (sheetBody && sheetBody.contains(row)) {
      // ROUND 15: inside the Tray Sheet the caption row lives in the sheet's
      // scrolling body, and that box — not the viewport — is what clips the
      // card. Same hazard as the sticky panel, same answer: measure it.
      box = sheetBody.getBoundingClientRect();
    } else if (bounded) {
      box = panel.getBoundingClientRect();
    } else {
      const vh = document.documentElement.clientHeight || window.innerHeight;
      let floor = vh;
      const bar = this.els.bar;
      if (bar && !bar.hidden) {
        const br = bar.getBoundingClientRect();
        // Only when it is actually pinned to the bottom — the same element sits
        // in the flow with the panel above 901px.
        if (br.height > 0 && br.bottom >= vh - 2) floor = br.top;
      }
      box = { top: 0, bottom: floor };
    }
    const r = row.getBoundingClientRect();
    const GAP = 8;
    const EDGE = 10;
    const below = box.bottom - r.bottom - GAP - EDGE;
    const above = r.top - box.top - GAP - EDGE;
    const up = below < INFO_MIN_H && above > below;
    pop.classList.toggle('nh-bb__pop--up', up);
    pop.style.setProperty(
      '--nh-bb-pop-max',
      `${Math.max(INFO_MIN_H, Math.floor(up ? above : below))}px`
    );
  },

  comboOpen(combo) {
    const btn = combo.querySelector('[data-nh-bb-combo-btn]');
    return btn ? btn.getAttribute('aria-expanded') === 'true' : false;
  },

  /** A combo's listbox, wherever the Tray Sheet has put it. */
  comboList(combo) {
    if (combo === this.els.capCombo && this.els.capList) return this.els.capList;
    return combo.querySelector('[data-nh-bb-combo-list]');
  },

  toggleCombo(combo, open) {
    const btn = combo.querySelector('[data-nh-bb-combo-btn]');
    const list = this.comboList(combo);
    if (!btn || !list) return;
    // ROUND 16 (Sina) — REMOVED: the "Your cap" grid inside the Tray Sheet, and
    // with it the special case that opened the sheet instead of the listbox
    // when the cap tile was tapped in sheet mode. Colourway switching on a
    // phone now has two routes only — the on-cap thumbnails in the strip
    // (which set the cap you buy too, since round 15.1) and the cap tile,
    // which falls straight through to the centred `<dialog>` chooser below,
    // exactly like `band` mode always has. One listbox, one set of options.
    // Only one open at a time: two overlapping popups in a 460px column is a
    // mess, and closing the other one is what a native select would do.
    if (open) {
      this.combos.forEach((other) => {
        if (other !== combo && this.comboOpen(other)) this.toggleCombo(other, false);
      });
    }
    // BELOW 901px THE CAP LIST IS A MODAL, not an anchored popup (round 11,
    // Sina). Anchored, it was clipped inside the sticky bar — the round-8 trap
    // one layer down — and no offset fixes a clip. A <dialog> opened with
    // showModal() paints in the browser's TOP LAYER, which no ancestor's
    // overflow, transform or containment can reach.
    if (combo === this.els.capCombo && this.els.capModal && this.mq && this.mq.matches) {
      btn.setAttribute('aria-expanded', String(!!open));
      combo.classList.toggle('is-open', !!open);
      if (open) this.openCapModal(list);
      else this.closeCapModal(list);
      return;
    }
    btn.setAttribute('aria-expanded', String(!!open));
    list.hidden = !open;
    combo.classList.toggle('is-open', !!open);
    if (!open) return;
    const current = list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]');
    if (current) current.focus();
  },

  /**
   * Open the phone's cap chooser.
   *
   * The listbox is MOVED into the dialog, never cloned: one <ul>, one set of
   * options, one aria-selected. `showModal()` supplies the ::backdrop, the focus
   * trap and Escape; what it does not supply is a scroll lock, so the document
   * gets one — a modal over a page that still scrolls under it reads as broken
   * on a phone.
   */
  openCapModal(list) {
    const dlg = this.els.capModal;
    const body = this.els.capModalBody;
    if (!dlg || !body || typeof dlg.showModal !== 'function') {
      // No <dialog> support: fall back to the anchored popup rather than to
      // nothing at all.
      list.hidden = false;
      return;
    }
    if (list.parentElement !== body) body.appendChild(list);
    list.hidden = false;
    list.classList.add('nh-bb__combo-list--modal');
    this.scrollLock = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dlg.showModal();
    const current = list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]');
    if (current) current.focus();
  },

  /** Put the listbox back where the desktop layout expects it. */
  closeCapModal(list) {
    const dlg = this.els.capModal;
    const combo = this.els.capCombo;
    list.hidden = true;
    list.classList.remove('nh-bb__combo-list--modal');
    if (dlg && dlg.open) dlg.close();
    document.documentElement.style.overflow = this.scrollLock || '';
    this.scrollLock = null;
    // Back inside the combo, before the dialog, so the anchored popup works
    // unchanged the moment the window is widened past 901px.
    if (combo && dlg && list.parentElement !== combo) combo.insertBefore(list, dlg);
  },

  onComboKeydown(e, combo) {
    const list = this.comboList(combo);
    const btn = combo.querySelector('[data-nh-bb-combo-btn]');
    const opts = Array.from(list.querySelectorAll('[role="option"]'));
    if (!opts.length) return;
    const at = opts.indexOf(document.activeElement);
    let next = null;
    if (e.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(at + 1, opts.length - 1);
    else if (e.key === 'ArrowUp') next = at <= 0 ? 0 : at - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = opts.length - 1;
    else if (e.key === 'Escape') {
      e.preventDefault();
      // ROUND 17 (item 3): the round-15 branch that made Escape from a cap row
      // park the SHEET is gone with the in-sheet grid — the rows live in the
      // centred <dialog> again, so Escape closes the dialog and nothing else.
      // (This keydown's preventDefault also suppresses the dialog's own
      // `cancel`, so the close has to happen here.)
      this.toggleCombo(combo, false);
      if (btn) btn.focus();
      return;
    } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      if (at >= 0) this.chooseOption(combo, opts[at]);
      return;
    } else if (e.key === 'Tab') {
      // Tabbing out is a dismissal, not a choice.
      this.toggleCombo(combo, false);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    opts[next].focus();
  },

  chooseOption(combo, opt) {
    const btn = combo.querySelector('[data-nh-bb-combo-btn]');

    // Only cap rows live here now: the bundle switcher moved to the page header
    // and is the shared <nordenheim-bundle-picker>, which owns its own popup.
    const capKey = opt.dataset.nhBbCapopt;
    if (!capKey) return;
    this.selectCap(capKey);
    // ROUND 14 — BELOW 901px THE TWO CAPS BECOME ONE.
    //
    // `capKey` (what you buy) and `viewCap` (what you look at) are deliberately
    // separate, so that comparing a plate across colourways cannot change the
    // order. On DESKTOP that split is legible: the cap you chose is a photograph
    // in the dropdown, right beside the stage. On a PHONE the pinned 375x150
    // band is the only cap on screen — so choosing Björk and still seeing
    // Faluröd is the interface contradicting the choice you just made.
    //
    // Choosing from the modal therefore also moves the view. The gallery strip
    // still changes `viewCap` alone, so looking is still free; it is only the
    // deliberate purchase choice that pulls the picture with it.
    if (this.compactWidth()) this.applyCap(capKey, { animate: true });
    // ROUND 17 (Sina, item 3) — ONE TAP SELECTS AND CLOSES. Round 15 returned
    // early here in sheet mode, because the options were then a grid INSIDE
    // the sheet and closing meant nothing. Round 16 put the phone's chooser
    // back in the centred <dialog>, and the early return left it open after a
    // choice — the shopper had to close it by hand. toggleCombo() closes the
    // dialog (closeCapModal) and focus returns to the cap tile that opened it.
    this.toggleCombo(combo, false);
    if (btn) btn.focus();
  },

  /**
   * The line under the picture: what the stage is showing, with both nouns as
   * links.
   *
   * ANY caption that PAIRS a plate with a cap reads "<Plate> × <Cap>" (round
   * 10, Sina) — the live combination and a worn shot whose colourway is known.
   * The two names are the line, and a multiplication sign says how they go
   * together in a mark that needs no translation. The sentence itself is not
   * thrown away: every word of the template that is not a token is kept in the
   * DOM, visually hidden, so a screen reader still hears "<Plate> shown on
   * <Cap>" or "<Plate> as worn on <Cap>", and their Swedish. The × is
   * aria-hidden for the same reason.
   *
   * A caption that names NO cap is not a pairing and keeps its words —
   * "<Plate> as worn", "<Plate> on its own". The test is the template's own
   * shape (both tokens present, and a cap to fill the second one), so a
   * variant added later needs no change here.
   *
   * Three shapes, because the stage has three states and one of them ("as worn
   * on Björk") must never be mistaken for the combination being bought. A shot
   * with no recorded colourway gets the neutral wording rather than a guess:
   * CLAUDE.md records that the positional convention for those was wrong on half
   * the library.
   *
   * The anchors are built here rather than shipped in the markup because their
   * hrefs are only known once a plate is previewed — and an anchor that ships as
   * href="#" is dead for the life of the page, the theme's a11y module having
   * bound a permanent preventDefault to it at load.
   */
  paintCapNote() {
    const note = this.els.capNote;
    if (!note) return;
    // ROUND 20 — THE PANEL CAN BE SHOWING A CAP AND NO PLATE.
    //
    // Mix & Match's cap row opens one cap on its own: its own photographs, and
    // a caption that is just its name. There is no plate in that state, so the
    // "{plate} x {cap}" sentence below has nothing to join and the info mark
    // (which is About <plate>) has nothing to describe.
    //
    // Asked of the HOST, not inferred: the bundle PDPs have no such state and
    // define no `soloCap`, so they never reach this branch and their caption is
    // byte-for-byte what it was.
    const solo = typeof this.soloCap === 'function' ? this.soloCap() : null;
    if (solo) {
      note.textContent = '';
      // ROUND 21, ITEM D — THE CAP GETS THE SAME MARK. Round 20 hid it here
      // ("no plate, so no About <plate> button"); the caps have descriptions of
      // their own and the caption row is the row that offers them. Passing the
      // cap SUBJECT rather than null also keeps syncInfo's own contract: the
      // handle changes, so a card left open on the plate we just left is closed
      // before the new one is named. A cap with an empty body still hides the
      // mark — syncInfo tests the body, not the state.
      this.syncInfo(this.capInfoSubject(solo));
      const capTpl = note.getAttribute('data-nh-bb-note-cap') || '';
      if (!capTpl) return;
      capTpl.split(/(\{cap\})/).forEach((part) => {
        if (part === '{cap}') note.appendChild(this.noteLink(solo.name || '', solo.url || ''));
        else if (part) note.appendChild(document.createTextNode(part));
      });
      return;
    }
    const cap = this.cap(this.viewCap);
    const plate = this.previewedPlate();
    const shots = this.shotsFor(plate);
    const shot = this.shotIndex >= 0 ? shots[this.shotIndex] : null;

    let tpl = note.getAttribute('data-nh-bb-note-live') || '';
    let capForLine = cap;
    if (shot && shot.art) {
      tpl = note.getAttribute('data-nh-bb-note-art') || '';
      capForLine = null;
    } else if (shot && shot.packaging) {
      // ROUND 16 (Sina) — the packaging shot wrongly read "<Plate> as worn":
      // it fell into the worn/worn-plain branch below, which the caption
      // template shares with no other case. It names no cap and is not a
      // worn photo, so it gets its own template — plain, no {cap} token —
      // rather than reusing "as worn" for something nobody is wearing.
      tpl = note.getAttribute('data-nh-bb-note-pack') || '';
      capForLine = null;
    } else if (shot) {
      const shotCap = shot.cap ? this.cap(shot.cap) : null;
      tpl = shotCap
        ? note.getAttribute('data-nh-bb-note-worn') || ''
        : note.getAttribute('data-nh-bb-note-worn-plain') || '';
      capForLine = shotCap;
    }
    const sep = note.getAttribute('data-nh-bb-note-sep') || '';
    // Does this line pair the two names? Asked of the template rather than of
    // which branch we came down, so nothing has to be kept in step.
    const joins = !!(sep && capForLine
      && tpl.indexOf('{plate}') >= 0 && tpl.indexOf('{cap}') >= 0);

    note.textContent = '';
    this.syncInfo(plate);
    if (!tpl || !plate) return;

    // Split on both tokens at once so their order in the sentence is the
    // template's business, not this function's — Swedish may not put them in the
    // same places. The × goes BETWEEN whichever two the template names, for the
    // same reason.
    let seen = 0;
    tpl.split(/(\{plate\}|\{cap\})/).forEach((part) => {
      if (part === '{plate}' || part === '{cap}') {
        // The plate is the half that truncates, so it is the half that carries
        // its full name in a title — the cap's is always shown whole.
        const node = part === '{plate}'
          ? this.noteLink(plate.name || '', plate.url || '', true)
          : (capForLine ? this.noteLink(capForLine.name || '', capForLine.url || '') : null);
        if (!node) return;
        if (joins && seen) note.appendChild(this.noteTimes(sep));
        note.appendChild(node);
        seen += 1;
      } else if (part) {
        note.appendChild(joins ? srText(part) : document.createTextNode(part));
      }
    });
  },

  /** The visible separator. Decoration to a screen reader; srText carries the
   *  words it stands in for. */
  noteTimes(text) {
    const el = document.createElement('span');
    el.className = 'nh-bb__times';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = text;
    return el;
  },

  /** A named link, or plain text when there is nothing to link to. */
  noteLink(text, href, isPlate) {
    if (!href) return document.createTextNode(text);
    const a = document.createElement('a');
    a.className = 'nh-bb__notelink' + (isPlate ? ' nh-bb__notelink--plate' : '');
    a.setAttribute('href', href);
    if (isPlate) a.setAttribute('title', text);
    a.textContent = text;
    return a;
  },

  /* ------------------------------------------------------------- gallery */

  /**
   * The plate's own photographs, in the order they earn their place.
   *
   * Worn and packaging shots first, then the artwork LAST. The artwork goes last
   * on purpose: the shopper has just clicked that very image in the wall, so
   * leading with it opens onto the picture they were already looking at.
   *
   * The artwork is modelled as one more shot rather than as a separate stage
   * mode, so it flows through openShot, the strip and the caption with no new
   * state. Its `cap` is empty because it is cap-independent — which is already
   * the "cannot say which colourway" case the caption handles.
   */
  shotsFor(plate) {
    const shots = ((plate && plate.editorial) || []).slice();
    const full = plate && (plate.artFull || plate.art);
    if (full) {
      shots.push({
        full,
        // The wall has already fetched `art`, so this swatch is a cache hit.
        thumb: (plate && plate.art) || full,
        alt: (plate && plate.name) || '',
        cap: '',
        art: true,
      });
    }
    return shots;
  },

  /**
   * Rebuild the strip under the picture for the previewed plate.
   *
   * Two kinds of chip in one row: this plate on each of the eight colourways,
   * then its own photographs. Tapping a colourway chip changes what is SHOWN
   * (`viewCap`: stage + caption) and never what is bought (`capKey`) — on
   * desktop since 2026-09-02, on the phone's sheet again since round 17.
   */
  renderStrip() {
    const strip = this.els.strip;
    if (!strip) return;
    // ROUND 20 — the band can hold a set that is not a plate's.
    //
    // `stripView()` is the host's answer to "am I showing something else?" and
    // returns { shots, name } or null. Mix & Match returns the open cap's own
    // photographs while its cap row has one open; the bundle PDPs define no
    // such method and take the plate path exactly as before.
    //
    // A solo view gets NO colourway chips: eight thumbnails of this plate on
    // eight caps is the one thing rule 1 says must not appear beside a cap.
    const solo = (typeof this.stripView === 'function' && this.stripView()) || null;
    const plate = solo ? null : this.previewedPlate();
    if (!solo && !plate) {
      strip.hidden = true;
      strip.textContent = '';
      return;
    }

    // The group's accessible name is a sentence about what is in it, so it
    // moves with the contents. Both strings are settings carried on the
    // element; nothing here holds English.
    const stripName = strip.dataset.nhBbStripLabel;
    const stripCapName = strip.dataset.nhBbStripCapLabel;
    if (stripName) {
      strip.setAttribute('aria-label', solo && stripCapName ? stripCapName : stripName);
    }

    const frag = document.createDocumentFragment();
    let any = false;

    // Mix & Match's order (Sina, 2026-09-02): the plate's own photographs — worn
    // shots and the packaging — then the artwork on its own, then the plate on
    // each of the eight colourways. The artwork sits after the photographs
    // because the shopper has just clicked that exact image in the wall.
    (solo ? solo.shots : this.shotsFor(plate)).forEach((shot, i) => {
      if (!shot.thumb) return;
      any = true;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nh-bb__chip' + (shot.art ? ' nh-bb__chip--art' : '');
      btn.dataset.nhBbShot = String(i);
      btn.style.setProperty('--nh-bb-chip-img', `url("${cssUrl(shot.thumb)}")`);
      btn.setAttribute('aria-pressed', String(this.shotIndex === i));
      const name = document.createElement('span');
      name.className = 'nh-bb__sr';
      if (solo) {
        // The chips are one object photographed five ways and the alt text
        // cannot tell them apart (see the section's own comment: Liquid's `alt`
        // falls back to the product title, so it is suppressed there and
        // arrives empty). The name is the cap's, and the second and later chips
        // carry the photograph's number so no two are announced alike — the
        // defect round 18.1 recorded for the three worn shots. A digit needs no
        // setting and has no English in it, so nothing here is untranslated.
        name.textContent = shot.alt
          || (i === 0 ? (solo.name || '') : `${solo.name || ''} ${i + 1}`.trim());
      } else if (shot.art) {
        name.textContent = fill(this.labels.artLabel, { plate: plate.name || '' });
      } else {
        // ROUND 19.1 — ONE RULE FOR EVERY PHOTOGRAPH OF THE PLATE, and it does
        // not read the image's alt text.
        //
        // Two defects in the line this replaces (`shot.alt || plate.name`).
        // The worn shots carry NO alt — Liquid's `alt` falls back to the
        // product title, so the payload suppresses it — which made three chips
        // in a row announce the identical name and gave a screen-reader user
        // nothing to tell them apart. The packaging shot DOES carry one, and it
        // is English on all 196 of them: a product image is not a translatable
        // resource and its alt is a separate PRODUCT_IMAGE one that has never
        // been written (CLAUDE.md, Data conventions), so that chip read English
        // on /sv and no setting could change it.
        //
        // The plate's own name, numbered from the second — exactly the rule
        // round 20 applies to a cap's own photographs in the solo branch above.
        // A digit needs no setting and carries no English in it. What each shot
        // actually IS stays where it is described properly: the caption, which
        // has its own template per kind ("as worn on {cap}", the packaging
        // line, "on its own") and its own Swedish.
        const base = plate.name || '';
        name.textContent = i === 0 ? base : `${base} ${i + 1}`.trim();
      }
      btn.appendChild(name);
      frag.appendChild(btn);
    });

    (solo ? [] : this.data.caps || []).forEach((cap) => {
      const src = thumbSrc({ images: plate.images }, cap.key);
      if (!src) return;
      any = true;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nh-bb__chip nh-bb__chip--cap';
      btn.dataset.nhBbChipCap = cap.key;
      btn.style.setProperty('--nh-bb-chip-img', `url("${cssUrl(src)}")`);
      btn.setAttribute('aria-pressed', String(this.shotIndex < 0 && cap.key === this.viewCap));
      const name = document.createElement('span');
      name.className = 'nh-bb__sr';
      name.textContent = fill(this.labels.capOn, { cap: cap.name || cap.key });
      btn.appendChild(name);
      frag.appendChild(btn);
    });

    strip.textContent = '';
    strip.appendChild(frag);
    // ROUND 16 (perf) — while the sheet is parked and has never yet been
    // opened, the strip stays hidden regardless of content: `hidden` is
    // `display:none`, the one state that does not fetch a CSS background, so
    // the ~13 chip images built above do not cost a request nobody can see.
    // setSheet(true) re-runs this function once, on the first real open.
    //
    // ONE assignment, not two. This used to be `strip.hidden = !any;` followed
    // by a second line forcing it back to `true` when deferred — which, for a
    // fraction of a millisecond, set `hidden` to `false` first. `hidden` was
    // already `true` (the server-rendered default) going into this call, and
    // toggling it false-then-true-again in the SAME synchronous function does
    // NOT read as "never shown" to the browser: a style/layout pass can fire
    // between those two statements and start fetching the backgrounds just
    // appended, and the second assignment cannot cancel a request already
    // under way. Measured: all 8 cap-thumb chips + the packaging chip fetched
    // on a cold sheet-mode landing despite `strip.hidden` reading `true`
    // immediately afterward — exactly this. Computing the FINAL value once
    // means `hidden` never passes through `false` when it is meant to stay
    // `true` throughout.
    //
    // Any `display` on a class outranks the UA [hidden] rule, so the
    // stylesheet restates display:none for this element at class strength.
    const deferred = this.sheetActive() && !this.sheetEverOpened;
    strip.hidden = deferred ? true : !any;
  },

  markStrip() {
    const strip = this.els.strip;
    if (!strip) return;
    strip.querySelectorAll('[data-nh-bb-chip-cap]').forEach((b) => {
      b.setAttribute('aria-pressed',
        String(this.shotIndex < 0 && b.dataset.nhBbChipCap === this.viewCap));
    });
    strip.querySelectorAll('[data-nh-bb-shot]').forEach((b) => {
      b.setAttribute('aria-pressed', String(Number(b.dataset.nhBbShot) === this.shotIndex));
    });
  },

  openShot(i) {
    const shots = this.shotsFor(this.previewedPlate());
    const shot = shots[i];
    if (!shot) return;
    // Colourway first: applyCap resets shotIndex and repaints, so setting the
    // index before it would be undone a line later.
    if (shot.cap && shot.cap !== this.viewCap) this.applyCap(shot.cap, { animate: false });
    this.shotIndex = i;
    this.paintStage();
    this.paintCapNote();
    this.markStrip();
  },

};

/**
 * Copy the core onto a section's prototype.
 *
 * `owns` names the methods the host keeps for itself — a deliberate override,
 * declared rather than discovered. Anything else that would be clobbered is
 * reported and left alone: a silent clobber of syncBuy or setSheet is a cart
 * bug and a stranded sheet, not a styling difference, and it would show up as
 * "the bundle posted one line" long after the change that caused it.
 */
function installBundleCore(proto, { owns = [] } = {}) {
  Object.keys(NhBundleCore).forEach((key) => {
    const mine = Object.prototype.hasOwnProperty.call(proto, key);
    if (mine && owns.indexOf(key) < 0) {
      // eslint-disable-next-line no-console
      console.error(`[nordenheim-bundle-core] "${key}" already exists on this prototype and is not declared in owns[] — the core copy was NOT installed.`);
      return;
    }
    if (!mine) proto[key] = NhBundleCore[key];
  });
}

window.NordenheimBundleCore = { NhBundleCore, installBundleCore, clamp, cssUrl, fill, thumbSrc };
})();
